// pages/superadmin/SalesWorkspace.jsx
//
// Phase 5A/5B, Sales Workspace. Rendered as the "Sales Agent" tab inside
// CustomerDashboard (embedded), and standalone via a `backTo`. Shows today's
// AI-generated Daily Revenue Briefing and lets a rep prepare/approve/send AI
// outreach drafts per opportunity. All data scoping (which
// opportunities/companies are visible) happens server-side in
// revenueBriefingController/outreachController based on the logged-in user,
// this component itself has no role-specific logic, so it serves both the
// internal team and Phase 5B partners.
//
// Styling deliberately mirrors CustomerDashboard.jsx / TeamManagement.jsx
// (same tokens, same header, same table + card patterns) rather than
// introducing a new visual language for one page.
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import useRevenueApi from "../../../hooks/useRevenueApi";
import { scopeCss } from "../../../utils/scopeCss";
import { createPortal } from "react-dom";
import useSubnavSlot from "../../../components/useSubnavSlot";
import { SearchField } from "../../../arc/components/search-field/search-field";
import { Input } from "../../../arc/components/input/input";
import { Textarea } from "../../../arc/components/textarea/textarea";
import { Checkbox } from "../../../arc/components/checkbox/checkbox";
import { Select } from "../../../arc/components/select/select";
import { Button } from "../../../arc/components/button/button";
import RouteSuggestion from "../../../components/RouteSuggestion";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../../arc/components/tabs/tabs";
import { Skeleton } from "../../../arc/components/skeleton/skeleton";
import { ThemeSwitch } from "../../../arc/components/theme-switch/theme-switch";
import { applyThemeWithTransition } from "../../../utils/themeTransition";
import InfoTooltip from "../../../components/InfoTooltip";
import OpenBriefModal from "../../../components/OpenBriefModal";
import BulkOutreachModal from "../../../components/BulkOutreachModal";
import {
  productLabel as humanProductLabel,
  healthSourceLabel,
  accountsTypeLabel,
  humaniseSystemValues,
  actionLabel as humanActionLabel,
} from "../../../utils/labels";

const EMBED_SCOPE = ".ri-embed-sales";

// Reset lives outside `styles` on purpose. scopeCss prefixes every selector
// with EMBED_SCOPE, which would give a bare "*" reset class-level specificity
// and let it override padding/margin set by Arc components and the modals
// (OpenBriefModal, BulkOutreachModal). :where() keeps it at zero specificity,
// so any component class rule wins.
const RESET_CSS = `:where(.dash-root, ${EMBED_SCOPE}) :where(*, *::before, *::after) { box-sizing: border-box; margin: 0; padding: 0; }`;

const styles = `
  /* Cal.com-style design system, mirrors CustomerDashboard / TeamManagement.
   * White canvas, monochrome-flat cards, hairline borders, no gradients,
   * accent locked to AXO navy.
   */




  .dash-root.dark { color-scheme: dark; }
  .dash-root.dark .settings-menu { background: var(--surface); border-color: var(--border); }
  .dash-root.dark .settings-row { color: var(--text-muted); }
  .dash-root.dark .settings-row:hover { background: var(--surface-raised); color: var(--text); }
  .dash-root.dark .table-toolbar { background: var(--surface-raised); }
  .dash-root.dark .field-input { background: var(--bg); }
  .dash-root.dark .toggle-track { background: var(--border-strong); }
  .dash-root.dark .summary-card { background: var(--surface); }
  .dash-root.dark .stat-pill { background: var(--surface-raised); }

  html, body { overflow-x: hidden; }
  .dash-root { min-height: 100vh; background: var(--bg); color: var(--text); font-family: var(--sans); font-size: var(--text-base); line-height: 1.5; -webkit-font-smoothing: antialiased; overflow-x: clip; overflow-wrap: anywhere; }

  /* ── HEADER, flat AXO navy, matches CustomerDashboard ─────────────── */
  .dash-header { background: var(--header-bg); border-bottom: 1px solid var(--header-border); position: sticky; top: 0; z-index: 100; }
  .dash-header-inner { max-width: 1580px; margin: 0 auto; padding: 0 28px; height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 20px; }
  .dash-brand { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
  .dash-brand-logo { width: 54px; height: 54px; border-radius: var(--radius); overflow: hidden; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.13); padding: 4px; }
  .dash-brand-logo img { width: 100%; height: 100%; object-fit: contain; }
  .dash-brand-text { display: flex; flex-direction: column; gap: 2px; }
  .dash-brand-name { font-size: 14px; font-weight: 600; letter-spacing: -0.01em; color: #ffffff; }
  .dash-brand-sub { font-size: 13px; font-weight: 500; letter-spacing: 0; color: var(--header-text-muted); }
  .dash-nav { display: flex; align-items: center; gap: 6px; margin-left: auto; }

  .btn-back { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; background: var(--header-btn-bg); border: 1px solid var(--header-btn-border); border-radius: var(--radius-sm); color: var(--header-text); transition: background var(--transition), border-color var(--transition), color var(--transition); text-decoration: none; }
  .btn-back:hover { background: var(--header-btn-hover-bg); border-color: rgba(255,255,255,0.22); color: #ffffff; }

  .dash-header .btn-ghost { background: var(--header-btn-bg); border: 1px solid var(--header-btn-border); color: var(--header-text); }
  .dash-header .btn-ghost:hover { background: var(--header-btn-hover-bg); border-color: rgba(255,255,255,0.22); color: #ffffff; }
  .dash-header .user-pill { background: var(--header-pill-bg); border: 1px solid var(--header-btn-border); color: var(--header-text); }
  .dash-header .online-dot { background: var(--green); box-shadow: 0 0 5px rgba(16,185,129,0.5); }
  .dash-header .settings-menu { background: var(--accent); border-color: rgba(255,255,255,0.1); box-shadow: var(--shadow-md); }
  .dash-header .settings-row { color: rgba(255,255,255,0.68); }
  .dash-header .settings-row:hover { background: rgba(255,255,255,0.08); color: #ffffff; }

  /* BUTTONS */
  .btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 36px; padding: 0 16px; border: 1px solid transparent; border-radius: 8px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; line-height: 1.45; cursor: pointer; white-space: nowrap; text-decoration: none; outline: none; -webkit-tap-highlight-color: transparent; transition: background var(--transition), color var(--transition), border-color var(--transition), box-shadow var(--transition), opacity var(--transition); }
  .btn:focus-visible { box-shadow: 0 0 0 3px var(--accent-dim); }
  .btn:disabled { opacity: 0.52; cursor: not-allowed; }
  .btn-sm { min-height: 32px; padding: 0 12px; }
  .btn-md { min-height: 36px; padding: 0 16px; }
  .btn-lg { min-height: 40px; padding: 0 20px; }
  .btn-ghost { background: transparent; border: 1px solid var(--border); color: var(--text-muted); }
  .btn-ghost:hover { background: var(--surface-raised); border-color: var(--border-strong); color: var(--text); }
  .btn-primary { background: var(--accent); color: #fff; border: 1px solid transparent; letter-spacing: 0.01em; }
  .btn-primary:hover { background: var(--accent-hover); }
  .btn-primary:disabled { background: var(--text-faint); color: var(--surface); border-color: transparent; cursor: not-allowed; opacity: 0.55; }
  .btn-primary:disabled:hover { background: var(--text-faint); }
  .btn-secondary { background: transparent; border: 1px solid var(--border); color: var(--text-muted); }
  .btn-secondary:hover { background: var(--surface-raised); color: var(--text); }

  /* USER PILL */
  .user-pill { display: flex; align-items: center; gap: 7px; padding: 5px 11px; background: var(--surface-raised); border: 1px solid var(--border); border-radius: 100px; font-size: 13px; color: var(--text-muted); font-weight: 500; }
  .online-dot { width: 6px; height: 6px; background: var(--green); border-radius: 50%; flex-shrink: 0; }

  /* SETTINGS DROPDOWN */
  .settings-wrap { position: relative; }
  .settings-menu { display: none; position: absolute; right: 0; top: calc(100% + 8px); min-width: 214px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); box-shadow: var(--shadow-lg); z-index: 200; padding: 5px; overflow: hidden; }
  .settings-menu.open { display: block; }
  .settings-row { display: flex; align-items: center; justify-content: space-between; padding: 9px 12px; font-size: var(--text-sm); color: var(--text-muted); border-radius: var(--radius-sm); cursor: pointer; }
  .settings-row:hover { background: var(--surface-raised); color: var(--text); }
  .settings-divider { height: 1px; background: var(--border); margin: 5px 0; }

  /* TOGGLE */
  .toggle { position: relative; width: 30px; height: 16px; flex-shrink: 0; }
  .toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
  .toggle-track { position: absolute; inset: 0; background: var(--border-strong); border-radius: 9999px; cursor: pointer; transition: background var(--transition); }
  .toggle-track::after { content: ''; position: absolute; width: 12px; height: 12px; left: 2px; top: 2px; background: white; border-radius: 50%; transition: transform var(--transition); box-shadow: 0 1px 2px rgba(0,0,0,0.2); }
  .toggle input:checked ~ .toggle-track { background: var(--accent-mid); }
  .toggle input:checked ~ .toggle-track::after { transform: translateX(14px); }

  /* MAIN LAYOUT */
  .dash-main { max-width: 1580px; margin: 0 auto; padding: 24px 28px 40px; display: flex; flex-direction: column; gap: 20px; }

  .dashboard-greeting { padding: 2px 2px 4px; }
  .dashboard-greeting-text { font-family: "Cal Sans", var(--sans); font-size: 36px; font-weight: 600; line-height: 1.15; letter-spacing: -1px; color: var(--text); }
  @media (max-width: 767px) { .dashboard-greeting-text { font-size: 28px; letter-spacing: -0.5px; } }

  @media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; scroll-behavior: auto !important; } }
  /* SEGMENTED CONTROL, nav-pill-group: surface-soft track, white sliding pill */
  .seg { position: relative; display: inline-flex; max-width: 100%; gap: 2px; padding: 4px; background: var(--surface-raised); border-radius: 9999px; overflow-x: auto; scrollbar-width: none; }
  .seg::-webkit-scrollbar { display: none; }
  .seg-pill { position: absolute; top: 4px; bottom: 4px; left: 0; border-radius: 9999px; background: var(--surface); box-shadow: 0 1px 2px rgba(0,0,0,0.08); pointer-events: none; }
  .seg-pill.ready { transition: transform 0.24s cubic-bezier(0.16, 1, 0.3, 1), width 0.24s cubic-bezier(0.16, 1, 0.3, 1); }
  .seg-btn { position: relative; z-index: 1; min-height: 36px; padding: 6px 14px; background: transparent; border: none; border-radius: 9999px; font: inherit; font-size: 13px; font-weight: 500; color: var(--text-muted); cursor: pointer; white-space: nowrap; transition: color var(--transition); }
  .seg-btn:hover:not(.active) { color: var(--text); }
  .seg-btn.active { color: var(--text); }
  .seg-count { margin-left: 6px; font-size: 13px; color: var(--text-faint); font-variant-numeric: tabular-nums; }
  .dash-root.dark .seg-pill { background: #2f2f2f; box-shadow: none; }

  /* CHIP GROUP, badge-pill filters; selected chip fills ink and shows a check */
  .chips { display: flex; flex-wrap: wrap; gap: 8px; }
  .chip { display: inline-flex; align-items: center; gap: 0; min-height: 36px; padding: 6px 14px; background: var(--surface); border: 1px solid var(--border); border-radius: 9999px; font: inherit; font-size: 13px; font-weight: 500; color: var(--text); cursor: pointer; white-space: nowrap; transition: background var(--transition), border-color var(--transition), color var(--transition); }
  .chip:hover:not(.active) { background: var(--surface-raised); border-color: var(--border-strong); }
  .chip.active { background: var(--ink); border-color: var(--ink); color: var(--on-ink); }
  .chip-check { width: 0; opacity: 0; margin-right: 0; transition: width 0.18s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.18s, margin 0.18s; flex-shrink: 0; }
  .chip.active .chip-check { width: 12px; opacity: 1; margin-right: 6px; }
  .chip-count { margin-left: 8px; font-size: 13px; font-variant-numeric: tabular-nums; color: var(--text-faint); }
  .chip.active .chip-count { color: inherit; opacity: 0.7; }

  /* SKELETON, placeholder pulse, staggered; static under reduced motion */
  .skel { background: var(--surface-sunken); border-radius: 8px; animation: skel-pulse 1.4s ease-in-out infinite; }
  @keyframes skel-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }
  .skel-title { width: 220px; max-width: 60%; height: 32px; margin-bottom: 24px; }
  .skel-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(168px, 1fr)); gap: 16px; margin-bottom: 48px; }
  .skel-tile { height: 112px; border-radius: 12px; }
  .skel-label { width: 140px; height: 22px; margin-bottom: 16px; }
  .skel-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 24px; }
  .skel-card { display: flex; flex-direction: column; gap: 12px; padding: 24px; border: 1px solid var(--border); border-radius: 12px; }
  .skel-pill { width: 96px; height: 24px; border-radius: 9999px; }
  .skel-line { height: 14px; }
  .skel-line.w70 { width: 70%; height: 18px; }
  .skel-line.w50 { width: 50%; }
  @media (max-width: 767px) { .skel-tiles { grid-template-columns: 1fr; margin-bottom: 32px; } }
  @media (prefers-reduced-motion: reduce) { .skel { animation: none; } .seg-pill.ready, .chip-check { transition: none; } }

  @keyframes ws-card-in { from { opacity: 0; transform: translateY(2px); } to { opacity: 1; transform: translateY(0); } }
  .dash-main > .summary-card, .dash-main > .table-wrap, .dash-main > .empty-state { animation: ws-card-in 0.3s ease both; }

  /* SUMMARY CARD */
  .summary-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 32px; transition: box-shadow var(--transition); }
  .summary-title { display: flex; align-items: center; gap: 4px; font-family: "Cal Sans", var(--sans); font-size: 28px; font-weight: 600; letter-spacing: -0.5px; line-height: 1.2; color: var(--text); margin-bottom: 8px; }
  .brief-generated { font-size: 12px; color: var(--text-faint); margin-bottom: 22px; }
  .summary-text { white-space: pre-wrap; font-size: 16px; line-height: 1.5; color: var(--text-body); }

  /* Today's Briefing, Cal.com spec.
     KPI tiles = feature-card (gray surface-card, abstract numbers).
     Priority cards = product-mockup-card (white + hairline, real content).
     Tone is carried by a 6px dot inside a badge-pill, never by tinted fills. */
  .kpi-strip { display: grid; grid-template-columns: repeat(auto-fit, minmax(168px, 1fr)); gap: 16px; }
  .kpi-tile { display: flex; flex-direction: column; gap: 8px; padding: 24px; background: var(--surface-sunken); border-radius: 12px; min-width: 0; --dot: var(--text-faint); }
  .kpi-tile-head { display: flex; align-items: center; gap: 8px; }
  .kpi-tile-icon { display: none; }
  .kpi-tile-label { display: inline-flex; align-items: center; gap: 4px; font-size: 14px; font-weight: 500; line-height: 1.4; color: var(--text-muted); }
  .kpi-tile-label::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--dot); flex-shrink: 0; }
  .kpi-tile-value { font-family: "Cal Sans", var(--sans); font-size: 36px; font-weight: 600; letter-spacing: -1px; line-height: 1.15; color: var(--text); font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
  .kpi-tile-sub { font-size: 13px; font-weight: 500; line-height: 1.4; color: var(--text-faint); }
  .kpi-tile-scope { font-size: 11px; font-weight: 600; letter-spacing: 0.02em; color: var(--text-faint); opacity: 0.85; }
  .kpi-tile.green { --dot: var(--green); }
  .kpi-tile.amber { --dot: var(--amber); }
  .kpi-tile.red { --dot: var(--red); }
  .brief-block { margin-bottom: 48px; }
  .brief-block:last-child { margin-bottom: 0; }
  .brief-section-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 16px; min-height: 28px; }
  .brief-section-head .brief-section-label, .brief-block > .brief-section-label { margin-bottom: 16px; }
  .brief-section-head .brief-section-label { margin-bottom: 0; }
  .brief-section-label { display: flex; align-items: center; gap: 4px; font-size: 18px; font-weight: 600; line-height: 1.4; color: var(--text); }
  .brief-section-count { font-size: 13px; font-weight: 500; color: var(--text-faint); font-variant-numeric: tabular-nums; }
  .pool-note { margin-top: 14px; font-size: 12.5px; line-height: 1.5; color: var(--text-faint); }
  .pool-note b { font-weight: 600; color: var(--text-muted); font-variant-numeric: tabular-nums; }
  .brief-empty { padding: 32px; text-align: center; font-size: 14px; color: var(--text-muted); border: 1px dashed var(--border); border-radius: 12px; }
  .dash-root.dark .kpi-tile { background: var(--surface-raised); }
  @media (max-width: 767px) { .summary-title { font-size: 24px; } .kpi-strip { grid-template-columns: 1fr; } .brief-block { margin-bottom: 32px; } .kpi-tile-value { font-size: 28px; } }

  .brief-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 24px; }
  .brief-card { display: flex; flex-direction: column; gap: 16px; padding: 24px; background: var(--surface); border: 1px solid var(--border); border-radius: 12px; box-shadow: 0 1px 2px rgba(0,0,0,0.05); --dot: var(--text-faint); }
  .brief-card-head { display: flex; align-items: center; gap: 8px; margin-bottom: -4px; }
  .brief-card-head > .axo-info-tip { margin-inline-start: -4px; }
  .flag > .axo-info-tip { margin-inline-start: -2px; }
  .brief-icon { display: none; }
  .brief-eyebrow { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 9999px; background: var(--surface-sunken); font-size: 13px; font-weight: 500; line-height: 1.4; color: var(--text); }
  .brief-eyebrow::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--dot); flex-shrink: 0; }
  .brief-score { margin-left: auto; display: inline-flex; align-items: center; justify-content: center; min-width: 28px; height: 28px; padding: 0 8px; border-radius: 9999px; background: var(--surface-sunken); color: var(--text); font-size: 13px; font-weight: 500; font-variant-numeric: tabular-nums; }
  .brief-card-title { font-size: 18px; font-weight: 600; letter-spacing: -0.3px; line-height: 1.4; color: var(--text); }
  .brief-kpi { display: flex; align-items: baseline; gap: 8px; }
  .brief-kpi-value { font-family: "Cal Sans", var(--sans); font-size: 28px; font-weight: 600; letter-spacing: -0.5px; line-height: 1.2; color: var(--text); font-variant-numeric: tabular-nums; }
  .brief-kpi-label { display: inline-flex; align-items: center; gap: 4px; font-size: 13px; font-weight: 500; color: var(--text-faint); }
  .brief-turnover { font-size: 13px; font-weight: 500; color: var(--text-muted); }
  .brief-card-body { margin: 0; font-size: 14px; line-height: 1.5; color: var(--text-body); display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
  .brief-summary-strip { margin-bottom: 20px; padding: 16px 18px; background: var(--surface-sunken); border: 1px solid var(--border); border-radius: 12px; }
  .brief-summary-strip-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; font-size: 12px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-muted); margin-bottom: 12px; }
  .brief-summary-strip-head span { font-size: 11px; font-weight: 500; letter-spacing: 0; text-transform: none; color: var(--text-faint); }
  .brief-summary-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(135px, 1fr)); gap: 10px; }
  .brief-summary-kpi { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 12px 14px; }
  .brief-summary-kpi-label { font-size: 11px; font-weight: 600; letter-spacing: 0.03em; text-transform: uppercase; color: var(--text-muted); }
  .brief-summary-kpi-value { font-size: 24px; font-weight: 600; letter-spacing: -0.03em; color: var(--text); margin-top: 4px; }
  .brief-summary-kpi-sub { font-size: 11.5px; color: var(--text-faint); margin-top: 2px; }
  .brief-show-more { display: flex; justify-content: center; margin-top: 18px; }
  .brief-notes-details { margin-top: 12px; border-top: 1px solid var(--border); padding-top: 10px; }
  .brief-notes-details summary { cursor: pointer; font-size: 12px; font-weight: 600; color: var(--text-muted); }
  .brief-notes-details p { margin: 10px 0 0; font-size: 13px; line-height: 1.6; color: var(--text); white-space: pre-wrap; }
  .brief-card.green { --dot: var(--green); }
  .brief-card.amber { --dot: var(--amber); }
  .brief-card.red { --dot: var(--red); }
  .brief-card.blue { --dot: #3b82f6; }
  .brief-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px 16px; flex-wrap: wrap; margin-bottom: 16px; }
  .brief-sort { display: inline-flex; align-items: center; }
  .brief-sort > div { display: flex; flex-direction: row; align-items: center; gap: 8px; }
  .brief-sort label { font-size: 13px; font-weight: 500; line-height: 1.4; color: var(--text-muted); white-space: nowrap; }
  .brief-sort button[role="combobox"] { width: auto; min-width: 168px; height: 40px; min-height: 40px; padding: 0 14px; gap: 12px; border: 1px solid var(--border); border-radius: 8px; background: var(--surface); color: var(--text); font-family: var(--sans); font-size: 14px; font-weight: 400; line-height: 1.4; }
  .brief-sort button[role="combobox"]:focus-visible { outline: none; border-color: var(--text); }
  .brief-rank { font-size: 13px; font-weight: 500; color: var(--text-faint); font-variant-numeric: tabular-nums; }
  /* The global reset (* { padding: 0 }) strips padding from Arc components, so set it explicitly here. */
  .brief-footer > :not(.brief-open) { display: inline-flex; align-items: center; min-width: 0; max-width: 100%; padding: 6px 12px; line-height: 1.4; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .brief-footer .brief-open { margin-left: auto; flex-shrink: 0; min-height: 32px; padding: 0 14px; line-height: 1.4; }
  .brief-empty { display: flex; flex-direction: column; align-items: center; gap: 12px; }
  .brief-flags { align-items: center; display: flex; flex-wrap: wrap; gap: 8px; }
  .brief-footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding-top: 16px; border-top: 1px solid var(--border); margin-top: auto; }

  .inline-error { display: flex; align-items: center; gap: 8px; padding: 12px 16px; border: 1px solid var(--border); border-radius: 8px; background: var(--surface); font-size: 14px; color: var(--text); }
  .inline-error::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--red); flex-shrink: 0; }
  .stat-row { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 18px; }
  .stat-pill { display: flex; flex-direction: column; gap: 3px; padding: 12px 18px; background: var(--surface-raised); border: 1px solid var(--border); border-radius: var(--radius-sm); min-width: 128px; }
  .stat-pill-label { font-size: 13px; font-weight: 500; letter-spacing: 0; color: var(--text-faint); }
  .stat-pill-value { font-size: 19px; font-weight: 600; letter-spacing: -0.3px; color: var(--text); font-variant-numeric: tabular-nums; }

  /* TABLE WRAP */
  .table-wrap { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }

  /* SEARCH ROW */
  .search-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: 0; }
  .search-input-wrap { position: relative; flex: 1 1 280px; max-width: 380px; }
  .search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--text-faint); pointer-events: none; }
  .search-input { width: 100%; height: 40px; padding: 0 36px 0 36px; background: var(--bg); border: 1px solid var(--border); border-radius: 8px; font-family: var(--sans); font-size: 14px; color: var(--text); outline: none; transition: border-color var(--transition), box-shadow var(--transition); }
  .search-input::placeholder { color: var(--text-faint); }
  .search-input:focus { border-color: var(--text); box-shadow: none; }
  .search-input::-webkit-search-cancel-button { display: none; }
  .search-clear { position: absolute; right: 7px; top: 50%; transform: translateY(-50%); width: 20px; height: 20px; border: none; background: transparent; color: var(--text-faint); font-size: 16px; line-height: 1; cursor: pointer; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
  .search-clear:hover { background: var(--surface-hover); color: var(--text); }
  .search-count { font-size: 13px; font-weight: 500; color: var(--text-faint); flex-shrink: 0; font-variant-numeric: tabular-nums; }

  /* PAGINATION */
  .pagination-row { display: flex; align-items: center; justify-content: center; gap: 16px; padding: 16px 0 0; }
  .pagination-status { font-size: 13px; font-weight: 500; color: var(--text-muted); font-variant-numeric: tabular-nums; }

  /* OPPORTUNITY LIST, Cal.com-style: hairline cards, near-black actions,
   * sentence-case labels, no tinted chips. One company open at a time. */
  .opp-list { display: flex; flex-direction: column; gap: 12px; }
  .opp-card {
    --ink-btn: #111111; --ink-btn-active: #242424; --on-ink: #ffffff;
    background: var(--surface); border: 1px solid var(--border); border-radius: 12px; overflow: hidden;
  }
  .dash-root.dark .opp-card { --ink-btn: var(--accent); --ink-btn-active: var(--accent-hover); --on-ink: #ffffff; }
  .opp-card.open { box-shadow: 0 1px 2px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.05); border-color: var(--border-strong); }

  .opp-head-row { display: flex; align-items: stretch; }
  .opp-select { display: flex; align-items: center; padding-left: 16px; flex: 0 0 auto; }
  .opp-head-row .opp-head { flex: 1; min-width: 0; padding-left: 8px; }
  .opp-head { display: grid; grid-template-columns: 36px minmax(0,1fr) auto; align-items: center; gap: 14px; width: 100%; padding: 16px 20px; background: transparent; border: none; text-align: left; font: inherit; color: inherit; cursor: pointer; }
  .opp-head:focus-visible { outline: none; box-shadow: inset 0 0 0 2px var(--border-strong); }
  .opp-avatar { width: 36px; height: 36px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 500; color: var(--text); flex-shrink: 0; }
  .opp-head-main { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
  .opp-title { font-size: 15px; font-weight: 600; letter-spacing: -0.01em; color: var(--text); }
  .opp-meta { font-size: 13px; color: var(--text-muted); }
  .opp-head-side { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
  .opp-score { font-size: 13px; font-weight: 500; color: var(--text-muted); font-variant-numeric: tabular-nums; padding: 0 4px; }
  .opp-chevron { color: var(--text-faint); transition: transform var(--transition); flex-shrink: 0; }
  .opp-card.open .opp-chevron { transform: rotate(180deg); }

  /* BULK OUTREACH */
  .bulk-bar { display: flex; align-items: center; gap: 10px 14px; flex-wrap: wrap; padding: 12px 16px; background: var(--surface); border: 1px solid var(--border); border-radius: 12px; }
  .bulk-bar-count { font-size: 13px; font-weight: 600; color: var(--text); font-variant-numeric: tabular-nums; }
  .bulk-bar-hint { font-size: 13px; color: var(--text-muted); }
  .bulk-bar-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-left: auto; }
  .draft-edit { display: flex; flex-direction: column; gap: 10px; }
  .draft-edit-label { display: block; margin-bottom: 4px; font-size: 11px; font-weight: 600; letter-spacing: 0.03em; text-transform: uppercase; color: var(--text-faint); }
  .draft-edit-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .draft-edit-note { font-size: 12px; line-height: 1.5; color: var(--text-muted); }

  /* badge-pill: neutral fill, ink text, a small dot carries the meaning */
  .flag, .opp-card .reply-badge { display: inline-flex; align-items: center; gap: 6px; font-family: var(--sans); font-size: 13px; font-weight: 500; letter-spacing: 0; text-transform: none; padding: 3px 10px; border-radius: 9999px; background: var(--surface-sunken); border: none; color: var(--text); white-space: nowrap; cursor: default; }
  .flag::before, .opp-card .reply-badge::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--dot, var(--text-faint)); }
  .flag.red { --dot: var(--red); }
  .flag.green { --dot: var(--green); }
  .flag.blue { --dot: #3b82f6; }
  .flag.amber { --dot: var(--amber); }
  .opp-card .reply-badge.positive { --dot: var(--green); background: var(--surface-sunken); color: var(--text); }
  .opp-card .reply-badge.negative, .opp-card .reply-badge.unsubscribe { --dot: var(--red); background: var(--surface-sunken); color: var(--text); }
  .opp-card .reply-badge.question { --dot: var(--amber); background: var(--surface-sunken); color: var(--text); }

  .opp-body { padding: 20px 20px 24px 118px; border-top: 1px solid var(--border); }

  /* nav-pill-group */
  .opp-tabs-wrap { margin-bottom: 24px; }
  .seg-btn:focus-visible, .chip:focus-visible, .brief-sort button[role="combobox"]:focus-visible { outline: 2px solid var(--text); outline-offset: 2px; }

  .opp-pane { display: flex; flex-direction: column; gap: 28px; }
  .pane-label { font-size: 14px; font-weight: 600; color: var(--text); letter-spacing: 0; text-transform: none; }
  .pane-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }

  .facts { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 18px 24px; }
  .fact dt { font-size: 13px; color: var(--text-muted); margin-bottom: 2px; }
  .fact dd { font-size: 14px; color: var(--text); }
  .fact dd.warn { color: var(--red); }
  .fact dd.conf-high, .fact dd.conf-medium, .fact dd.conf-low { text-transform: capitalize; }
  .fact dd.conf-low { color: var(--red); }
  .facts-empty, .pane-empty { font-size: 14px; color: var(--text-muted); }

  .opp-notes { display: flex; flex-direction: column; gap: 8px; }
  .opp-note { display: flex; gap: 10px; align-items: flex-start; font-size: 14px; line-height: 1.5; color: var(--text); padding: 12px 14px; border-radius: 8px; background: var(--surface-sunken); }
  .opp-note::before { content: ''; width: 6px; height: 6px; margin-top: 8px; border-radius: 50%; background: var(--dot, var(--text-faint)); flex-shrink: 0; }
  .opp-note.red { --dot: var(--red); }
  .opp-note.amber { --dot: var(--amber); }

  /* Outreach UX polish */
  .opp-card .draft-generating { display: flex; align-items: center; gap: 12px; padding: 20px; background: var(--surface-sunken); border-radius: 12px; font-size: 14px; color: var(--text); }
  .opp-card .draft-generating-sub { color: var(--text-muted); font-size: 13px; margin-top: 2px; }
  .spin-dot { width: 16px; height: 16px; border-radius: 50%; border: 2px solid var(--border); border-top-color: var(--text); animation: spin-dot 0.7s linear infinite; flex-shrink: 0; }
  @keyframes spin-dot { to { transform: rotate(360deg); } }
  .opp-card .contact-gap { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: 14px 16px; border: 1px dashed var(--border); border-radius: 8px; font-size: 13px; color: var(--text-muted); }
  .opp-card .draft-recipient { font-size: 13px; color: var(--text-muted); margin-top: 8px; }
  .axo-notice { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); z-index: 1000; background: var(--text); color: var(--surface); padding: 10px 18px; border-radius: 8px; font-size: 14px; box-shadow: var(--shadow-md); animation: draft-in 0.2s ease; }
  .axo-notice.error { background: var(--red); color: #fff; }
  .draft-setup { display: flex; flex-direction: column; gap: 16px; }
  .route-banner { display: flex; flex-direction: column; gap: 6px; padding: 16px 18px; border: 1px solid var(--border); border-radius: 10px; background: var(--surface); }
  .route-banner-label { font-size: 14px; font-weight: 600; line-height: 1.4; color: var(--text); }
  .route-banner-reason { font-size: 13px; line-height: 1.55; color: var(--text-muted); }
  .draft-picker { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; padding: 4px 0; }
  .draft-list { display: flex; flex-direction: column; gap: 12px; }
  .opp-card .draft-box { margin-top: 0; padding: 20px; background: var(--surface-sunken); border: none; border-radius: 12px; }
  .opp-card .draft-subject { font-size: 14px; }
  .opp-card .draft-content { font-size: 14px; color: var(--text); }
  .opp-card .draft-status { font-family: var(--sans); font-size: 13px; font-weight: 500; letter-spacing: 0; text-transform: capitalize; background: var(--surface); border: 1px solid var(--border); color: var(--text); display: inline-flex; align-items: center; gap: 6px; padding: 2px 10px; }
  .opp-card .draft-status::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--dot); }
  .opp-card .draft-status.draft { --dot: var(--amber); }
  .opp-card .draft-status.sent { --dot: var(--green); }
  .opp-card .draft-status.rejected { --dot: var(--red); }
  .opp-card .draft-status.draft, .opp-card .draft-status.sent, .opp-card .draft-status.rejected { background: var(--surface); border: 1px solid var(--border); color: var(--text); }
  .opp-card .draft-actions { margin-top: 16px; }

  .opp-card .cost-confirm { background: var(--surface-sunken); border: none; border-radius: 8px; padding: 2px 2px 2px 12px; gap: 8px; }
  .opp-card .cost-confirm-amount { font-family: var(--sans); font-size: 13px; color: var(--text); letter-spacing: 0; }

  .opp-footer { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; padding-top: 20px; border-top: 1px solid var(--border); }
  .outcome-form { display: inline-flex; gap: 8px; align-items: center; flex-wrap: wrap; }
  .opp-card .outcome-value-input, .opp-card .channel-sms-input { height: 36px; font-family: var(--sans); font-size: 14px; padding: 0 12px; border: 1px solid var(--border); border-radius: 8px; background: var(--surface); color: var(--text); outline: none; }
  .opp-card .outcome-value-input { width: 130px; }
  .opp-card .outcome-value-input:focus, .opp-card .channel-sms-input:focus { border-color: var(--text); box-shadow: none; }
  .outcome-confirmed { font-size: 13px; font-weight: 500; color: var(--text); display: inline-flex; align-items: center; gap: 6px; }
  .outcome-confirmed::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--green); }

  /* buttons inside a company card: 8px radius, 600 labels, black primary */
  .opp-card .btn { font-size: 13px; font-weight: 600; height: 36px; padding: 0 14px; border-radius: 8px; letter-spacing: 0; }
  .opp-card .btn-sm { height: 32px; padding: 0 12px; }
  .opp-card .btn-primary { background: var(--ink-btn); color: var(--on-ink); border: 1px solid transparent; }
  .opp-card .btn-primary:hover { background: var(--ink-btn); }
  .opp-card .btn-primary:active { background: var(--ink-btn-active); }
  .opp-card .btn-primary:disabled, .opp-card .btn-primary:disabled:hover { background: var(--border); color: var(--text-muted); opacity: 1; }
  .opp-card .btn-ghost { background: var(--surface); border: 1px solid var(--border); color: var(--text); }
  .opp-card .btn-ghost:hover { background: var(--surface); border-color: var(--border); color: var(--text); }
  .opp-card .btn-ghost:active { background: var(--surface-sunken); }
  .opp-card .btn-ghost:disabled { opacity: 0.5; }

  /* replies / research / channels panes */
  .opp-card .reply-subject { font-size: 14px; }
  .opp-card .reply-time, .opp-card .reply-from, .opp-card .channel-time, .opp-card .channel-provider { font-family: var(--sans); font-size: 13px; letter-spacing: 0; color: var(--text-muted); }
  .opp-card .reply-body { font-size: 14px; color: var(--text); }
  .opp-card .channel-row { font-size: 14px; }
  .opp-card .channel-status { font-family: var(--sans); font-size: 13px; letter-spacing: 0; text-transform: capitalize; color: var(--text-muted); }
  .opp-card .dossier { gap: 12px; }
  .opp-card .dossier-row { font-size: 14px; }
  .opp-card .dossier-key { font-family: var(--sans); font-size: 13px; font-weight: 400; letter-spacing: 0; text-transform: none; color: var(--text-muted); min-width: 110px; padding-top: 0; }
  .opp-card .dossier-questions ul { font-size: 14px; color: var(--text); }
  .opp-card .research-brief { background: var(--surface-sunken); border: none; border-radius: 12px; padding: 20px; }
  .opp-card .research-brief-top { font-family: var(--sans); font-size: 13px; font-weight: 500; letter-spacing: 0; text-transform: none; color: var(--text-muted); }
  .opp-card .research-brief-text { font-size: 14px; }

  /* COST CONFIRM */
  .cost-confirm { display: inline-flex; align-items: center; gap: 6px; background: var(--accent-dim); border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 4px 10px; }
  .cost-confirm-amount { font-size: 13px; font-variant-numeric: tabular-nums; color: var(--text-muted); }

  /* DRAFT BOX */
  @keyframes draft-in { from { opacity: 0; } to { opacity: 1; } }
  .draft-box { margin-top: 14px; padding: 16px; background: var(--surface-pearl); border: 1px solid var(--border); border-radius: var(--radius); animation: draft-in 0.2s ease; }
  .draft-subject { font-size: 13.5px; font-weight: 600; letter-spacing: -0.01em; color: var(--text); margin-bottom: 6px; }
  .draft-content { white-space: pre-wrap; font-size: 13.5px; color: var(--text-muted); line-height: 1.6; }
  .draft-status { display: inline-block; margin-top: 10px; font-size: 13px; font-weight: 500; text-transform: capitalize; padding: 4px 12px; border-radius: 9999px; }
  .draft-status.draft { background: var(--amber-dim); border: 1px solid var(--amber-border); color: var(--amber); }
  .draft-status.sent { background: var(--green-dim); border: 1px solid var(--green-border); color: var(--green); }
  .draft-status.rejected { background: var(--red-dim); border: 1px solid var(--red-border); color: var(--red); }
  .draft-actions { display: flex; gap: 8px; margin-top: 12px; }

  /* EMPTY / LOADING */
  .empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 20px; gap: 13px; text-align: center; }
  .empty-icon { font-size: 32px; opacity: 0.3; }
  .empty-title { font-size: 18px; font-weight: 600; color: var(--text); }
  .empty-sub { font-size: 14px; color: var(--text-muted); max-width: 360px; line-height: 1.5; }
  .empty-state-label { font-size: 14px; font-weight: 500; color: var(--text-muted); }
  .spinner { width: 26px; height: 26px; border: 2px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* OPPORTUNITY EXTRAS (replies / research / channels panes) */
  .opp-extras-panel { display: flex; flex-direction: column; gap: 14px; }
  .opp-extras-muted { font-size: var(--text-sm); color: var(--text-faint); }
  .opp-extras-error { margin-bottom: 12px; font-size: var(--text-sm); color: var(--red); }
  .opp-extras-note { margin-bottom: 12px; font-size: var(--text-sm); color: var(--green); }

  /* REPLY BADGES */
  .reply-badge { font-size: 13px; font-weight: 500; text-transform: capitalize; padding: 4px 12px; border-radius: 9999px; border: 1px solid var(--border); background: var(--surface-raised); color: var(--text-muted); }
  .reply-badge.positive { background: var(--green-dim); border-color: var(--green-border); color: var(--green); }
  .reply-badge.negative, .reply-badge.unsubscribe { background: var(--red-dim); border-color: var(--red-border); color: var(--red); }
  .reply-badge.question { background: var(--amber-dim); border-color: var(--amber-border); color: var(--amber); }
  .reply-row { padding: 10px 0; border-bottom: 1px solid var(--border); }
  .reply-row:last-child { border-bottom: none; }
  .reply-row-top { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
  .reply-subject { font-size: 13px; font-weight: 600; color: var(--text); }
  .reply-time { font-size: 13px; font-variant-numeric: tabular-nums; color: var(--text-faint); margin-left: auto; }
  .reply-from { font-size: 13px; color: var(--text-muted); margin-top: 2px; }
  .reply-body { font-size: 14px; color: var(--text-muted); margin-top: 5px; white-space: pre-wrap; max-height: 120px; overflow: auto; }

  /* DOSSIER */
  .dossier { display: flex; flex-direction: column; gap: 7px; }
  .dossier-row { display: flex; gap: 8px; font-size: 13px; }
  .dossier-key { font-size: 13px; font-weight: 500; color: var(--text-muted); min-width: 110px; padding-top: 2px; }
  .dossier-val { color: var(--text); }
  .dossier-questions ul { margin: 4px 0 0 16px; font-size: 13px; color: var(--text-muted); }
  .research-actions { display: flex; gap: 8px; }
  .research-brief { margin-top: 4px; padding: 12px 14px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); }
  .research-brief-top { display: flex; justify-content: space-between; font-size: 13px; font-weight: 500; color: var(--text-muted); margin-bottom: 8px; }
  .research-brief-text { white-space: pre-wrap; font-size: 13px; line-height: 1.6; color: var(--text); max-height: 280px; overflow: auto; }

  /* CHANNELS */
  .channel-actions-row { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
  .channel-sms-input { flex: 1 1 160px; min-width: 140px; font-family: var(--sans); font-size: 14px; padding: 6px 10px; border: 1px solid var(--border); border-radius: var(--radius-sm); background: var(--bg); color: var(--text); outline: none; transition: border-color var(--transition), box-shadow var(--transition); }
  .channel-sms-input:focus { border-color: var(--accent-mid); box-shadow: 0 0 0 2px var(--accent-dim); }
  .channel-row { display: flex; gap: 8px; align-items: center; font-size: 14px; color: var(--text-muted); }
  .channel-name { font-weight: 600; color: var(--text); }
  .channel-status { font-size: 13px; font-weight: 500; text-transform: capitalize; color: var(--text-muted); }
  .channel-provider { font-size: 13px; color: var(--text-faint); }
  .channel-time { font-size: 13px; font-variant-numeric: tabular-nums; color: var(--text-faint); margin-left: auto; }

  /* EMBED TOOLBAR */
  .ri-embed-toolbar { display: flex; justify-content: flex-end; margin-bottom: 16px; }

  @media (max-width: 720px) {
    .summary-card { padding: 20px; }
    .opp-head { padding: 14px 16px; gap: 12px; }
    .opp-body { padding: 16px 16px 20px; }
    .opp-head-side .flag { display: none; }
  }
  @media (max-width: 900px) {
    .dash-header-inner { padding: 0 16px; }
    .dash-main { padding: 16px 16px 32px; }
  }
  @media (max-width: 640px) {
    .search-input-wrap { max-width: none; flex-basis: 100%; }
  }
  @media (max-width: 480px) {
    .dash-brand-sub { display: none; }
  }
`;

const DRAFT_TYPE_LABELS = {
  outreach_email: "Email",
  outreach_linkedin: "LinkedIn",
  outreach_cold_call_intro: "Call script",
  outreach_follow_up: "Follow-up",
  meeting_prep_notes: "Meeting notes",
};

const GENERATING_LABELS = {
  outreach_email: "Creating your outreach email",
  outreach_linkedin: "Creating your LinkedIn message",
  outreach_cold_call_intro: "Creating your call script",
  outreach_follow_up: "Creating your follow-up email",
  meeting_prep_notes: "Creating your meeting notes",
};

// Keep the inline progress bar visible even if the API responds instantly
// (demo mode / a cached draft). Without this the start and finish state
// updates can batch into one render and the bar never paints.
const MIN_GENERATING_MS = 800;

function waitForVisibleProgress(startedAt) {
  const remaining = MIN_GENERATING_MS - (Date.now() - startedAt);
  return remaining > 0
    ? new Promise((resolve) => setTimeout(resolve, remaining))
    : Promise.resolve();
}

// draftType -> contact point it depends on (mirrors the backend guard).
// LinkedIn has no entry: it's rep-executed, and the "Copy & open LinkedIn"
// action falls back to a company search when no profile URL is on file.
const DRAFT_NEEDS = {
  outreach_email: "email",
  outreach_follow_up: "email",
  outreach_cold_call_intro: "phone",
};

function contactMissing(contact, draftType) {
  if (!contact) return null; // not loaded yet, don't block
  const need = DRAFT_NEEDS[draftType];
  if (!need) return null;
  const hasEmail =
    contact.hasValidEmail !== undefined
      ? contact.hasValidEmail
      : contact.hasEmail;
  const has = {
    email: hasEmail,
    linkedin: contact.hasLinkedin,
    phone: contact.hasPhone,
  }[need];
  return has ? null : need;
}

// The route suggestion is computed server-side, but the contact can change
// between that fetch and render (e.g. a validation/enrichment result). Pick
// the best ranked route whose contact point is actually available now, so the
// "Best route" banner and the highlighted draft button never disagree.
function effectiveRouteSuggestion(suggestion, contact) {
  const recommended = suggestion?.recommended;
  if (!recommended) return null;
  if (!contactMissing(contact, recommended.draftType)) return recommended;
  return (
    (suggestion.ranked || []).find(
      (route) => !contactMissing(contact, route.draftType),
    ) || null
  );
}

function draftSendIssue(draft, contact) {
  if (
    !draft ||
    !["outreach_email", "outreach_follow_up"].includes(draft.draftType)
  ) {
    return null;
  }
  if (!contact) return "Contact details have not loaded yet.";
  if (draft.contactChanged) {
    return "Contact changed since this draft was written. Regenerate it.";
  }
  if (!contact.hasValidEmail) {
    return `Email not verified: ${
      contact.emailValidation?.reason || "validation failed"
    }`;
  }
  if (!draft.recipientEmail) {
    return "This draft was written before contact verification. Regenerate it.";
  }
  if (draft.recipientEmail !== contact.email) {
    return "This draft is for an old contact. Regenerate it.";
  }
  return null;
}

const ACTION_LABELS = {
  send_initial_outreach: "Send initial outreach",
  send_follow_up: "Send follow-up",
  pitch_website_rebuild: "Pitch website rebuild",
  review_and_prioritise: "Review",
};

const STAGE_LABELS = {
  new: "New",
  contacted: "Contacted",
  qualified: "Qualified",
  proposal: "Proposal",
  won: "Won",
  lost: "Lost",
};

// The AI briefing arrives as free text ("short sections… keep it
// scannable"). Turn it into quick-to-identify boxes: blank-line separated
// sections become cards, a section that is purely a numbered/bulleted list
// becomes one card per item, and a "Heading\nbody" section gets a title.
// Falls back to a single card when the text has no obvious structure. The
// raw text is only used as a last resort so an unexpected format never
// renders blank.
function parseBriefingSections(text) {
  // Backend briefing copy still uses em-dashes; fold them to a plain dash so the
  // splitter below and the rendered cards never show one.
  const raw = String(humaniseSystemValues(text || ""))
    .replace(/\u2014/g, "-")
    .trim();
  if (!raw) return [];

  const LIST_RE = /^\s*(?:\d+[.)]|[-*•])\s+/;
  const stripList = (s) => s.replace(LIST_RE, "").trim();
  // "Acme Ltd - call today" → lead "Acme Ltd", detail "call today".
  const splitLead = (s) => {
    const m = String(s).match(/^(.{2,64}?)\s+[-–]\s+(.+)$/);
    if (m) return { title: m[1].trim(), body: m[2].trim() };
    return { title: null, body: String(s).trim() };
  };
  const sections = [];

  for (const block of raw.split(/\n\s*\n+/)) {
    const trimmed = block.trim();
    if (!trimmed) continue;
    const lines = trimmed
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

    // A block that is entirely list items → one box per item.
    if (lines.length > 1 && lines.every((l) => LIST_RE.test(l))) {
      lines.forEach((l) => sections.push(splitLead(stripList(l))));
      continue;
    }

    // "Heading\nbody…" → titled box.
    if (lines.length > 1) {
      const first = lines[0];
      const looksLikeHeading = first.length <= 64 && !/[.!?]$/.test(first);
      if (looksLikeHeading) {
        sections.push({
          title: first.replace(/:$/, ""),
          body: lines.slice(1).join(" "),
        });
        continue;
      }
    }

    sections.push(splitLead(stripList(trimmed).replace(/\s+/g, " ")));
  }

  return sections;
}

// Best-effort match of a briefing section back to the structured opportunity
// it names, so the card can carry real KPIs (score, £ value, flags) instead
// of just the prose. Longest company-name hit wins.
function matchOpportunity(section, opportunities) {
  const hay = `${section?.title || ""} ${section?.body || ""}`.toLowerCase();
  if (!hay.trim() || !Array.isArray(opportunities)) return null;
  let best = null;
  let bestLen = 0;
  for (const opp of opportunities) {
    const name = String(opp?.companyName || "")
      .trim()
      .toLowerCase();
    if (name && name.length > bestLen && hay.includes(name)) {
      best = opp;
      bestLen = name.length;
    }
  }
  return best;
}

// Signal tone from the card's own words, so a rep can triage by colour at a
// glance: red = going cold/at risk, amber = Companies House distress,
// green = live deal, blue = website signal, else the brand accent.
const PRODUCT_ACRONYMS = new Set(["axo", "6ela", "crm", "ai", "seo"]);
// Bucket key for opportunities the product scorer could not match to any
// product (primary null), mirrors "No clear opportunity identified" in the
// dashboard's Opportunity panel. Kept distinct so such opportunities are
// never attributed to a real product.
const UNCLASSIFIED_KEY = "_unclassified";
function productName(p) {
  return String(p || "")
    .replace(/_/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) =>
      PRODUCT_ACRONYMS.has(w.toLowerCase())
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
    )
    .join(" ");
}

function briefTone(text) {
  const t = String(text || "").toLowerCase();
  if (/overdue|insolven|dissolv|liquidat|administration|strike off/.test(t))
    return "amber";
  if (/live deal|qualified|proposal|meeting booked|booked in/.test(t))
    return "green";
  if (
    /going cold|\bcold\b|no response|not progressed|stalled|churn|at risk/.test(
      t,
    )
  )
    return "red";
  if (/website|redesign|rebuild|online presence|new site/.test(t))
    return "blue";
  return "accent";
}

// Companies House dates come back as plain YYYY-MM-DD; render them
// human-readably without pulling in a date library for one field.
function formatFilingDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatFetchedAt(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const hoursSince = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60));
  if (hoursSince < 1) return "just now";
  if (hoursSince < 24) return `${hoursSince}h ago`;
  const daysSince = Math.floor(hoursSince / 24);
  return `${daysSince}d ago`;
}

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// accounting_reference_date from CH is { day: "31", month: "12" }, the
// company's financial year-end, not a specific filing's date, so it can't
// go through formatFilingDate.
function formatAccountingReferenceDate(value) {
  if (!value || !value.day || !value.month) return null;
  const monthIdx = parseInt(value.month, 10) - 1;
  if (monthIdx < 0 || monthIdx > 11) return null;
  return `${value.day} ${MONTH_NAMES[monthIdx]}`;
}

// latestAccountsFiling.type is CH's short filing code (e.g. "AA",
// "AA01"); description is the long form. Prefer description, since "AA"
// means nothing to a salesperson, but fall back to type if CH didn't
// return one.
function formatFilingTypeLabel(filing) {
  if (!filing) return null;
  return accountsTypeLabel(filing.description || filing.type || null);
}

function formatDateTime(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Cal-style avatar: initials on a pastel fill, colour picked from the name
// so a company keeps the same tint between renders.
const AVATAR_TINTS = ["#fb923c", "#ec4899", "#8b5cf6", "#34d399", "#3b82f6"];
function avatarTint(name = "") {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_TINTS[h % AVATAR_TINTS.length];
}
function companyInitials(name = "") {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?"
  );
}

const REPLY_LABELS = {
  positive: "Positive",
  negative: "Negative",
  question: "Question",
  unsubscribe: "Unsubscribed",
  out_of_office: "Out of office",
  neutral: "Neutral",
};

const CHANNEL_LABELS = {
  linkedin: "LinkedIn",
  cold_call: "Call",
  sms: "SMS",
  whatsapp: "WhatsApp",
  other: "Other",
};

// Per-opportunity "previous conversations", company research and channel
// actions (Phase 5 items 3 and 5). Lazily loads each panel on first open so
// a briefing page with many cards doesn't fan out into dozens of requests.
function OpportunityExtras({ opp, activeTab, channelRefreshSignal = 0 }) {
  const api = useRevenueApi();
  const [loading, setLoading] = useState({
    replies: false,
    research: false,
    channels: false,
  });
  const [generating, setGenerating] = useState(false);
  const [estimating, setEstimating] = useState(false);
  // { logId, estimatedCostGbp } once the user has asked for a cost preview of
  // the research brief, no spend happens until they confirm.
  const [researchEstimate, setResearchEstimate] = useState(null);
  const [channelBusy, setChannelBusy] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [replies, setReplies] = useState(null);
  const [dossier, setDossier] = useState(null);
  const [research, setResearch] = useState(null);
  const [actions, setActions] = useState(null);
  const [smsBody, setSmsBody] = useState("");

  const setPanelLoading = (name, value) =>
    setLoading((prev) => ({ ...prev, [name]: value }));

  const loadReplies = async () => {
    setPanelLoading("replies", true);
    try {
      const res = await api.getInboundReplies(opp.customerCompanyId);
      setReplies(res.replies || []);
    } catch (err) {
      setError("Failed to load replies");
      setReplies([]);
    } finally {
      setPanelLoading("replies", false);
    }
  };

  const loadResearch = async () => {
    setPanelLoading("research", true);
    try {
      const [dossierRes, researchRes] = await Promise.all([
        api.getCompanyDossier(opp.customerCompanyId),
        api
          .getLatestCompanyResearch(opp.customerCompanyId)
          .catch(() => ({ research: null })),
      ]);
      setDossier(dossierRes.dossier || null);
      setResearch(researchRes.research || null);
    } catch (err) {
      setError("Failed to load research");
    } finally {
      setPanelLoading("research", false);
    }
  };

  const loadActions = async () => {
    setPanelLoading("channels", true);
    try {
      const res = await api.getChannelActions(opp.customerCompanyId);
      setActions(res.actions || []);
    } catch (err) {
      setError("Failed to load channel actions");
      setActions([]);
    } finally {
      setPanelLoading("channels", false);
    }
  };

  // A draft executed elsewhere for this opportunity ("Mark LinkedIn sent" /
  // "Mark call done") records a channel action server-side; if the Channels
  // panel is already open, refresh it so it doesn't go stale.
  useEffect(() => {
    if (channelRefreshSignal > 0 && actions !== null) {
      loadActions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelRefreshSignal]);

  // Lazy-load a pane the first time its tab is opened.
  useEffect(() => {
    setError(null);
    setNote(null);
    if (activeTab === "replies" && replies === null) loadReplies();
    if (activeTab === "research" && dossier === null) loadResearch();
    if (activeTab === "channels" && actions === null) loadActions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // AI Cost Transparency: the button now asks for a cost estimate first (no
  // provider call, no spend), shows it inline, and only spends on confirm.
  const handleRequestResearchEstimate = async () => {
    setEstimating(true);
    setError(null);
    try {
      const res = await api.estimateCompanyResearch(opp.customerCompanyId);
      setResearchEstimate(res.estimate);
    } catch (err) {
      setError(
        err?.response?.data?.message || "Failed to estimate research cost",
      );
    } finally {
      setEstimating(false);
    }
  };

  const handleCancelResearchEstimate = async () => {
    if (!researchEstimate) return;
    const { logId } = researchEstimate;
    setResearchEstimate(null);
    try {
      await api.rejectCompanyResearchEstimate(opp.customerCompanyId, logId);
    } catch (err) {
      // non-fatal, the log just sits as "estimated" with no cost incurred
    }
  };

  const handleConfirmResearchGenerate = async () => {
    if (!researchEstimate) return;
    const { logId } = researchEstimate;
    setGenerating(true);
    setResearchEstimate(null);
    setError(null);
    try {
      const res = await api.generateCompanyResearch(
        opp.customerCompanyId,
        logId,
      );
      setResearch({
        summaryText: res.summaryText,
        generatedAt: new Date().toISOString(),
        aiCostGbp: res.aiCostGbp,
      });
      window.dispatchEvent(new Event("ai-spend-changed"));
    } catch (err) {
      setError(
        err?.response?.data?.message || "Failed to generate research brief",
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleChannel = async (channel, body) => {
    setChannelBusy(true);
    setError(null);
    setNote(null);
    try {
      const res = await api.sendChannelAction({
        customerCompanyId: opp.customerCompanyId,
        channel,
        body,
      });
      setActions((prev) => (prev ? [res.action, ...prev] : [res.action]));
      setSmsBody("");
      setNote(
        res.sent
          ? `${CHANNEL_LABELS[channel] || channel} sent`
          : `${CHANNEL_LABELS[channel] || channel} logged`,
      );
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          `Failed to record ${CHANNEL_LABELS[channel] || channel} action`,
      );
    } finally {
      setChannelBusy(false);
    }
  };

  const panelId = (name) => `opp-extras-${opp.customerCompanyId}-${name}`;

  if (!["replies", "research", "channels"].includes(activeTab)) return null;

  return (
    <div className="opp-extras">
      {error && <div className="opp-extras-error">{error}</div>}
      {note && <div className="opp-extras-note">{note}</div>}

      <TabsContent
        value="replies"
        className="opp-extras-panel"
        id={panelId("replies")}
      >
        {loading.replies && replies === null ? (
          <Skeleton lines={3} label="Loading replies" />
        ) : !replies || replies.length === 0 ? (
          <div className="opp-extras-muted">No replies stored yet.</div>
        ) : (
          replies.map((reply) => (
            <div className="reply-row" key={reply.id}>
              <div className="reply-row-top">
                <span
                  className={`reply-badge ${reply.classification || "neutral"}`}
                >
                  {REPLY_LABELS[reply.classification] ||
                    reply.classification ||
                    "Unclassified"}
                </span>
                <span className="reply-subject">
                  {reply.subject || "(no subject)"}
                </span>
                <span className="reply-time">
                  {formatDateTime(reply.receivedAt)}
                </span>
              </div>
              <div className="reply-from">{reply.fromEmail}</div>
              {reply.body && <div className="reply-body">{reply.body}</div>}
            </div>
          ))
        )}
      </TabsContent>

      <TabsContent
        value="research"
        className="opp-extras-panel"
        id={panelId("research")}
      >
        {loading.research && dossier === null ? (
          <Skeleton lines={3} label="Loading research" />
        ) : !dossier ? (
          <div className="opp-extras-muted">No dossier available.</div>
        ) : (
          <div className="dossier">
            <div className="dossier-row">
              <span className="dossier-key">Industry</span>
              <span className="dossier-val">
                {(dossier.industry?.labels || []).join(", ") || "-"}
                {dossier.company?.sicCodes
                  ? ` (SIC ${dossier.company.sicCodes})`
                  : ""}
              </span>
            </div>
            <div className="dossier-row">
              <span className="dossier-key">Product fit</span>
              <span className="dossier-val">
                {dossier.productFit?.primaryLabel || "None"}
                {dossier.productFit?.mismatch ? " (mismatch)" : ""}
              </span>
            </div>
            <div className="dossier-row">
              <span className="dossier-key">Website</span>
              <span className="dossier-val">
                {dossier.website?.url || "-"} · health{" "}
                {dossier.website?.health ?? "-"}
                {dossier.website?.healthSource
                  ? ` (${healthSourceLabel(dossier.website.healthSource)})`
                  : ""}
              </span>
            </div>
            <div className="dossier-row">
              <span className="dossier-key">Financials</span>
              <span className="dossier-val">
                {dossier.company?.status || "-"}
                {dossier.financials?.accountsOverdue
                  ? " · accounts overdue"
                  : ""}
              </span>
            </div>
            {dossier.openQuestions?.length > 0 && (
              <div className="dossier-questions">
                <div className="dossier-key">Open questions</div>
                <ul>
                  {dossier.openQuestions.map((q, i) => (
                    <li key={i}>{q}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="research-actions">
          {researchEstimate ? (
            <span className="cost-confirm">
              <span className="cost-confirm-amount">
                Est. £
                {Number(researchEstimate.estimatedCostGbp || 0).toFixed(2)}
              </span>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={generating}
                onClick={handleConfirmResearchGenerate}
              >
                {generating ? "Generating…" : "Generate"}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={generating}
                onClick={handleCancelResearchEstimate}
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={generating || estimating}
              onClick={handleRequestResearchEstimate}
            >
              {generating
                ? "Generating…"
                : estimating
                  ? "Estimating…"
                  : research?.summaryText
                    ? "Regenerate AI brief"
                    : "Generate AI brief"}
            </button>
          )}
        </div>

        {research?.summaryText && (
          <div className="research-brief">
            <div className="research-brief-top">
              <span>AI research brief</span>
              {research.generatedAt && (
                <span>{formatDateTime(research.generatedAt)}</span>
              )}
            </div>
            <div className="research-brief-text">
              {humaniseSystemValues(research.summaryText)}
            </div>
          </div>
        )}
      </TabsContent>

      <TabsContent
        value="channels"
        className="opp-extras-panel"
        id={panelId("channels")}
      >
        <div className="channel-actions-row">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={channelBusy}
            onClick={() => handleChannel("linkedin")}
          >
            LinkedIn sent
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={channelBusy}
            onClick={() => handleChannel("cold_call")}
          >
            Call logged
          </button>
          <input
            className="channel-sms-input"
            placeholder="SMS body…"
            value={smsBody}
            onChange={(e) => setSmsBody(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={channelBusy || !smsBody.trim()}
            onClick={() => handleChannel("sms", smsBody.trim())}
          >
            Send SMS
          </button>
        </div>

        {loading.channels && actions === null ? (
          <Skeleton lines={3} label="Loading channels" />
        ) : !actions || actions.length === 0 ? (
          <div className="opp-extras-muted">No channel actions recorded.</div>
        ) : (
          actions.map((action) => (
            <div className="channel-row" key={action.id}>
              <span className="channel-name">
                {CHANNEL_LABELS[action.channel] || action.channel}
              </span>
              <span className="channel-status">{action.status}</span>
              {action.provider && (
                <span className="channel-provider">{action.provider}</span>
              )}
              <span className="channel-time">
                {formatDateTime(action.occurredAt)}
              </span>
            </div>
          ))
        )}
      </TabsContent>
    </div>
  );
}

// ── Plain-CSS controls (Cal spec: nav-pill-group / badge-pill / surface-card) ──
// Sliding-pill segmented control. Controlled; roving focus; arrow/Home/End keys.
// Used for compact value toggles that are NOT tied to panels (e.g. the signal
// filter). Peer views with panels use the Arc Tabs component instead.
function SegmentedControl({ label, options, value, onChange }) {
  const rootRef = useRef(null);
  const btnRefs = useRef({});
  const [pill, setPill] = useState({ x: 0, w: 0, ready: false });
  const optionsKey = options
    .map((o) => `${o.value}:${o.count ?? ""}`)
    .join("|");

  const measure = useCallback(() => {
    const el = btnRefs.current[value];
    if (!el) return;
    setPill((prev) =>
      prev.ready && prev.x === el.offsetLeft && prev.w === el.offsetWidth
        ? prev
        : { x: el.offsetLeft, w: el.offsetWidth, ready: true },
    );
  }, [value]);

  useLayoutEffect(() => {
    measure();
  }, [measure, optionsKey]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    return () => ro.disconnect();
  }, [measure]);

  const move = (idx) => {
    const next = options[(idx + options.length) % options.length];
    onChange(next.value);
    btnRefs.current[next.value]?.focus();
  };

  const onKeyDown = (e, idx) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") move(idx + 1);
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") move(idx - 1);
    else if (e.key === "Home") move(0);
    else if (e.key === "End") move(options.length - 1);
    else return;
    e.preventDefault();
  };

  return (
    <div className="seg" role="group" aria-label={label} ref={rootRef}>
      <span
        className={`seg-pill${pill.ready ? " ready" : ""}`}
        style={{ transform: `translateX(${pill.x}px)`, width: pill.w }}
        aria-hidden="true"
      />
      {options.map((o, i) => (
        <button
          key={o.value}
          type="button"
          ref={(el) => {
            btnRefs.current[o.value] = el;
          }}
          className={`seg-btn${o.value === value ? " active" : ""}`}
          aria-pressed={o.value === value}
          tabIndex={o.value === value ? 0 : -1}
          onClick={() => onChange(o.value)}
          onKeyDown={(e) => onKeyDown(e, i)}
        >
          {o.label}
          {o.count != null && <span className="seg-count">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

// Filter chips. Single-select by default and clearable (click the active chip).
function ChipGroup({ label, options, value, onChange }) {
  const refs = useRef([]);
  const focusIdx = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );

  const onKeyDown = (e, idx) => {
    let to = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") to = idx + 1;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") to = idx - 1;
    else if (e.key === "Home") to = 0;
    else if (e.key === "End") to = options.length - 1;
    if (to == null) return;
    e.preventDefault();
    refs.current[(to + options.length) % options.length]?.focus();
  };

  return (
    <div className="chips" role="group" aria-label={label}>
      {options.map((o, i) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            ref={(el) => {
              refs.current[i] = el;
            }}
            className={`chip${on ? " active" : ""}`}
            aria-pressed={on}
            tabIndex={i === focusIdx ? 0 : -1}
            onClick={() => onChange(on ? null : o.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            <svg
              className="chip-check"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
            <span>{o.label}</span>
            {o.count != null && <span className="chip-count">{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

// Loading placeholder for the briefing (Arc Skeleton reserves the space).
function BriefSkeleton() {
  return (
    <div className="summary-card">
      <Skeleton lines={6} label="Loading briefing" />
    </div>
  );
}

const SIZE_BAND_LABELS = {
  large: "large company",
  medium: "mid-sized company",
  small: "small company",
  micro: "micro company",
};

// Explains the card's Est. value: product deal value, scaled by company size
// (latest filed turnover / headcount) and weighted by priority score.
function estimatedValueHelp(opp) {
  const base =
    "AXO's estimate of what this opportunity is worth if it converts: the product's deal value, scaled for company size and weighted by the priority score.";
  if (!opp?.sizeBand) return base;
  if (opp.sizeBand === "estimated") {
    return `${base} No turnover is filed, so size is estimated from the company's accounts type, number of directors and age (×${opp.sizeMultiplier}).`;
  }
  if (opp.sizeBand === "unknown") {
    return `${base} Company size isn't known (no filed turnover or staff count), so the base deal value is used.`;
  }
  return `${base} Sized as a ${SIZE_BAND_LABELS[opp.sizeBand] || opp.sizeBand} (×${opp.sizeMultiplier}).`;
}

const PRIORITY_SCORE_HELP =
  "A 0–100 score from AXO's own rules, not the AI: it adds points for product fit, time since last contact, Companies House health, pipeline stage, replies and website changes, and subtracts points for contacting too recently or having no verified contact channel. Higher means more worth working today.";

export default function SalesWorkspace({
  backTo = "/super-admin",
  embedded = false,
  darkMode: hostDarkMode,
}) {
  const { user, logout } = useAuth();
  const serviceProfiles = useMemo(() => {
    const features = user?.customer?.features;
    if (
      Array.isArray(features?.service_profiles) &&
      features.service_profiles.length
    ) {
      return features.service_profiles;
    }
    return features?.service_profile ? [features.service_profile] : [];
  }, [user]);
  const api = useRevenueApi();
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [estimatingBriefing, setEstimatingBriefing] = useState(false);
  // Cost preview for regenerating today's briefing, no spend until confirmed.
  // { logId, estimatedCostGbp, opportunityCount, estimatedPipelineGbp }
  const [briefingEstimate, setBriefingEstimate] = useState(null);
  const [error, setError] = useState(null);
  const [drafts, setDrafts] = useState({}); // customerCompanyId -> [OutreachDraft]
  // Per-company request sequence. Opening the Outreach tab and generating a
  // draft both call the same endpoint; without this an older (pre-generation)
  // response can land last and wipe the freshly loaded draft list.
  const draftLoadRequestRef = useRef({});
  const [preparingKey, setPreparingKey] = useState(null); // `${ccId}:${draftType}`
  const [pendingEstimate, setPendingEstimate] = useState(null); // { key, customerCompanyId, draftType, logId, estimatedCostGbp, provider, model }
  const [estimatingKey, setEstimatingKey] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [contacts, setContacts] = useState({}); // customerCompanyId -> contact info
  const [routeSuggestions, setRouteSuggestions] = useState({}); // customerCompanyId -> { recommended, ranked }
  const [briefModal, setBriefModal] = useState(null); // { opp, section }
  const [briefModalLoading, setBriefModalLoading] = useState(false);
  const [findingEmailFor, setFindingEmailFor] = useState(null);
  const [notice, setNotice] = useState(null); // { text, tone }
  const flash = (text, tone = "ok") => {
    setNotice({ text, tone });
    setTimeout(() => setNotice(null), 4000);
  };
  // Bumped when a non-email draft is executed, so any open Channels panel
  // reloads its list (the action is recorded server-side).
  const [channelRefresh, setChannelRefresh] = useState(0);
  const [ownDarkMode, setDarkMode] = useState(
    () => localStorage.getItem("axo_theme") === "dark",
  );
  // Embedded in CustomerDashboard: the host owns the theme.
  const followsHost = embedded && hostDarkMode !== undefined;
  const darkMode = followsHost ? hostDarkMode : ownDarkMode;
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (followsHost) return;
    localStorage.setItem("axo_theme", ownDarkMode ? "dark" : "light");
  }, [ownDarkMode, followsHost]);

  useEffect(() => {
    if (!settingsOpen) return;
    const handler = () => setSettingsOpen(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [settingsOpen]);

  const loadBriefing = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getTodayBriefing();
      setBriefing(res.briefing);
    } catch (err) {
      if (err.response?.status === 404) {
        setBriefing(null);
      } else {
        setError("Failed to load briefing");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBriefing();
  }, [loadBriefing]);

  // Re-read today's AI spend after any AI action (draft, research). Updates
  // only the spend figure so the rest of the briefing isn't disturbed.
  const refreshSpend = useCallback(async () => {
    try {
      const res = await api.getTodayBriefing();
      setBriefing((prev) =>
        prev
          ? { ...prev, aiSpendTodayGbp: res.briefing?.aiSpendTodayGbp }
          : prev,
      );
    } catch (err) {
      // non-fatal, the tile just stays stale until the next load
    }
  }, []);

  useEffect(() => {
    window.addEventListener("ai-spend-changed", refreshSpend);
    return () => window.removeEventListener("ai-spend-changed", refreshSpend);
  }, [refreshSpend]);

  // AI Cost Transparency: "refresh/generate briefing" first asks for a cost
  // estimate (no provider call, no spend) and shows it for approval. Only
  // handleConfirmBriefingGenerate actually spends.
  const handleRequestBriefingEstimate = async () => {
    setEstimatingBriefing(true);
    setError(null);
    try {
      // T2 - Refresh first checks the workspace cache. Inside the 24-hour
      // window (and with unchanged underlying data) this costs nothing.
      const cached = await api.refreshBriefing?.();
      if (cached?.cached && cached.briefing) {
        setBriefing(cached.briefing);
        flash(
          `Served from cache · generated ${formatDateTime(
            cached.briefing.generatedAt,
          )}`,
        );
        return;
      }
      if (cached?.briefing) setBriefing(cached.briefing);

      const res = await api.estimateBriefing();
      if (!res.estimate) {
        flash("No opportunities found to brief on today");
        return;
      }
      setBriefingEstimate(res.estimate);
    } catch (err) {
      setError("Failed to estimate briefing cost");
    } finally {
      setEstimatingBriefing(false);
    }
  };

  const handleCancelBriefingEstimate = async () => {
    if (!briefingEstimate) return;
    const { logId } = briefingEstimate;
    setBriefingEstimate(null);
    try {
      await api.rejectBriefingEstimate(logId);
    } catch (err) {
      // non-fatal, the log just sits as "estimated" with no cost incurred
    }
  };

  const handleConfirmBriefingGenerate = async () => {
    if (!briefingEstimate) return;
    const { logId } = briefingEstimate;
    setGenerating(true);
    setBriefingEstimate(null);
    setError(null);
    try {
      const res = await api.generateBriefing(logId);
      setBriefing(res.briefing);
    } catch (err) {
      setError("Failed to generate briefing");
    } finally {
      setGenerating(false);
    }
  };

  const loadDraftsForCompany = async (customerCompanyId) => {
    const requestId = (draftLoadRequestRef.current[customerCompanyId] || 0) + 1;
    draftLoadRequestRef.current[customerCompanyId] = requestId;
    try {
      const res = await api.getOutreachDrafts(customerCompanyId);
      // A newer request for this company already landed — keep its result.
      if (draftLoadRequestRef.current[customerCompanyId] !== requestId) return;
      setDrafts((prev) => ({
        ...prev,
        [customerCompanyId]: (res.drafts || []).filter(
          (d) => d.status !== "rejected",
        ),
      }));
      if (res.contact) {
        setContacts((prev) => ({ ...prev, [customerCompanyId]: res.contact }));
      }
      if (res.routeSuggestion) {
        setRouteSuggestions((prev) => ({
          ...prev,
          [customerCompanyId]: res.routeSuggestion,
        }));
      }
    } catch (err) {
      // non-fatal, leave section empty, user can retry via prepare
    }
  };

  const openBriefModal = (opp, section) => {
    if (!opp) return;
    setBriefModal({ opp, section });
    const hasContact = Boolean(contacts[opp.customerCompanyId]);
    const hasDrafts = Boolean(drafts[opp.customerCompanyId]);
    if (!hasContact || !hasDrafts) {
      setBriefModalLoading(true);
      loadDraftsForCompany(opp.customerCompanyId).finally(() =>
        setBriefModalLoading(false),
      );
    }
  };

  // Phase 5 "AI Cost Transparency": clicking a draft-type button no longer
  // generates immediately. It first asks aiGateway (via /outreach/estimate)
  // for a cost estimate, no provider call yet, no cost incurred, and
  // shows it inline. Only handleConfirmGenerate actually spends money.
  const handleRequestEstimate = async (customerCompanyId, draftType) => {
    const key = `${customerCompanyId}:${draftType}`;
    setEstimatingKey(key);
    setError(null);
    try {
      const res = await api.estimateOutreachDraft(customerCompanyId, draftType);
      setPendingEstimate({
        key,
        customerCompanyId,
        draftType,
        ...res.estimate,
      });
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "MISSING_CONTACT") {
        flash(data.message, "error");
        loadDraftsForCompany(customerCompanyId);
      } else {
        setError(data?.message || "Failed to estimate draft cost");
      }
    } finally {
      setEstimatingKey(null);
    }
  };

  const handleFindEmail = async (customerCompanyId) => {
    const contact = contacts[customerCompanyId];
    if (!contact?.companyId) return;
    setFindingEmailFor(customerCompanyId);
    try {
      const beforeEmail = contact.email || null;
      const res = await api.triggerEnrichment(contact.companyId);
      const foundEmail = res?.data?.email || null;
      await loadDraftsForCompany(customerCompanyId);
      if (foundEmail && foundEmail !== beforeEmail) {
        flash(`Found verified email: ${foundEmail}`);
      } else if (!foundEmail && beforeEmail) {
        flash(
          `Removed ${beforeEmail} because it failed validation. No verified email found.`,
          "error",
        );
      } else if (foundEmail) {
        flash(`Email verified: ${foundEmail}`);
      } else {
        flash("No verified email found.");
      }
    } catch (err) {
      flash(
        err.response?.data?.message || "Couldn't search for contact details",
        "error",
      );
    } finally {
      setFindingEmailFor(null);
    }
  };

  // Copy the draft and open LinkedIn. Falls back to a LinkedIn search for the
  // company when no profile URL is on file, so the rep always has somewhere to
  // paste. The window is opened synchronously (before the async clipboard
  // call) so the browser doesn't treat it as a blocked popup.
  const handleCopyAndOpenLinkedIn = async (draft, contact, companyName) => {
    // Only ever open a real company page (linkedin.com/company/<slug>);
    // anything else (personal /in/ profiles, showcase pages, junk) falls
    // through to the company search below.
    const rawLinkedin = contact?.linkedinUrl?.trim();
    const direct =
      /^https?:\/\/([a-z0-9-]+\.)*linkedin\.com\/company\/[^/?#\s]+/i.test(
        rawLinkedin || "",
      )
        ? rawLinkedin
        : null;
    const query = companyName?.trim();
    const url =
      direct ||
      (query
        ? `https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(query)}`
        : "https://www.linkedin.com/");
    window.open(url, "_blank", "noopener,noreferrer");
    try {
      await navigator.clipboard.writeText(draft.content);
      flash(
        direct
          ? "Message copied, opening their LinkedIn page"
          : "Message copied, opening a LinkedIn search for this company",
      );
    } catch (e) {
      flash(
        "Opened LinkedIn, select the draft text and copy it manually",
        "error",
      );
    }
  };

  const handleCancelEstimate = async () => {
    if (!pendingEstimate) return;
    const { logId } = pendingEstimate;
    setPendingEstimate(null);
    try {
      await api.rejectOutreachEstimate(logId);
    } catch (err) {
      // non-fatal, the log just sits as "estimated" with no cost incurred
    }
  };

  const handleConfirmGenerate = async () => {
    if (!pendingEstimate) return;
    const { customerCompanyId, draftType, logId, key } = pendingEstimate;
    const startedAt = Date.now();
    setPreparingKey(key);
    // Keep the estimate row mounted while generating so the button itself
    // also reads "Generating…" alongside the inline progress bar.
    try {
      await api.prepareOutreachDraft(customerCompanyId, draftType, logId);
      await loadDraftsForCompany(customerCompanyId);
      refreshSpend();
      flash(
        `${GENERATING_LABELS[draftType]?.replace("Creating your ", "Your ")} is ready to review`,
      );
    } catch (err) {
      setError(err.response?.data?.message || "Failed to generate draft");
      refreshSpend(); // keep the tile in sync after a failed attempt too
    } finally {
      await waitForVisibleProgress(startedAt);
      setPreparingKey(null);
      setPendingEstimate(null);
    }
  };

  const handleApproveAndSend = async (customerCompanyId, draftId) => {
    setSendingId(draftId);
    try {
      const sendRes = await api.approveAndSendOutreach(draftId);
      await loadDraftsForCompany(customerCompanyId);
      markCompaniesContacted(customerCompanyId);
      flash(
        sendRes?.pipeline?.moved
          ? "Sent, moved to Contacted"
          : sendRes?.sentTo
            ? `Sent to ${sendRes.sentTo}`
            : "Done",
      );
      // Executing a LinkedIn/call draft also records a channel action, so
      // tell any open Channels panel to refresh.
      const draft = (drafts[customerCompanyId] || []).find(
        (d) => d.id === draftId,
      );
      if (
        draft &&
        ["outreach_linkedin", "outreach_cold_call_intro"].includes(
          draft.draftType,
        )
      ) {
        setChannelRefresh((n) => n + 1);
      }
    } catch (err) {
      flash(err.response?.data?.message || "Failed to send", "error");
    } finally {
      setSendingId(null);
    }
  };

  const handleReject = async (customerCompanyId, draftId) => {
    try {
      await api.rejectOutreach(draftId);
      // Hide it straight away; the reload below keeps state in sync.
      setDrafts((prev) => ({
        ...prev,
        [customerCompanyId]: (prev[customerCompanyId] || []).filter(
          (d) => d.id !== draftId,
        ),
      }));
      await loadDraftsForCompany(customerCompanyId);
    } catch (err) {
      setError("Failed to reject draft");
    }
  };

  const handleStartEditDraft = (draft) => {
    setEditingDraft({
      id: draft.id,
      draftType: draft.draftType,
      subject: draft.subject || "",
      content: draft.content || "",
    });
  };

  const handleCancelEditDraft = () => setEditingDraft(null);

  const handleSaveDraft = async (customerCompanyId) => {
    if (!editingDraft) return;
    if (!editingDraft.content.trim()) {
      flash("Draft body cannot be empty", "error");
      return;
    }
    setSavingDraftId(editingDraft.id);
    const isEmailDraft = ["outreach_email", "outreach_follow_up"].includes(
      editingDraft.draftType,
    );
    try {
      await api.updateOutreachDraft(editingDraft.id, {
        ...(isEmailDraft ? { subject: editingDraft.subject } : {}),
        content: editingDraft.content,
      });
      await loadDraftsForCompany(customerCompanyId);
      setEditingDraft(null);
      flash("Draft updated");
    } catch (err) {
      flash(err.response?.data?.message || "Failed to update draft", "error");
    } finally {
      setSavingDraftId(null);
    }
  };

  const handleBulkSent = (summary) => {
    const sentCompanyIds = (summary?.results || [])
      .filter((result) => result.success && result.customerCompanyId)
      .map((result) => result.customerCompanyId);
    if (sentCompanyIds.length > 0) markCompaniesContacted(sentCompanyIds);
    flash(
      `Bulk outreach: ${summary?.sent || 0} sent${
        summary?.failed ? `, ${summary.failed} failed` : ""
      }`,
      summary?.failed ? "error" : "ok",
    );
  };

  const openBulkOutreach = () => {
    setBulkOpportunities(selectedOpportunities);
    setBulkOpen(true);
  };

  const closeBulkOutreach = () => {
    setBulkOpen(false);
    setSelectedIds(new Set());
  };

  // Phase 5A/5B, manual outcome logging (feeds the AI Performance
  // Dashboard's Meetings Generated / Revenue Won metrics). Kept as a tiny
  // inline form per opportunity rather than a modal, this is meant to
  // be a 5-second action a rep does right after a call, not a separate
  // workflow.
  const [outcomeFormFor, setOutcomeFormFor] = useState(null); // customerCompanyId
  const [outcomeDealValue, setOutcomeDealValue] = useState("");
  const [recordingOutcome, setRecordingOutcome] = useState(false);
  const [outcomeConfirmedFor, setOutcomeConfirmedFor] = useState(null);

  const handleRecordOutcome = async (customerCompanyId, outcomeType) => {
    if (outcomeType === "deal_won" && !outcomeDealValue) return;
    setRecordingOutcome(true);
    try {
      await api.recordRevenueOutcome({
        customerCompanyId,
        outcomeType,
        valueGbp:
          outcomeType === "deal_won" ? Number(outcomeDealValue) : undefined,
      });
      setOutcomeFormFor(null);
      setOutcomeDealValue("");
      setOutcomeConfirmedFor(customerCompanyId);
      setTimeout(() => setOutcomeConfirmedFor(null), 2500);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to log outcome");
    } finally {
      setRecordingOutcome(false);
    }
  };

  // One company open at a time; tab resets to Overview on each open.
  const [expandedId, setExpandedId] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  // Bulk outreach: the selected companies (customerCompanyId) and the modal
  // that prepares/reuses one email draft per company before sending them in
  // a single approved batch.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  // Snapshot taken when the modal opens. The briefing is updated locally after
  // a successful send (pipeline/touch history), and without a snapshot that
  // new opportunities array would re-trigger the modal's data load mid-result.
  const [bulkOpportunities, setBulkOpportunities] = useState([]);
  // Inline draft editing. Editing a generated email before approving it is
  // part of the human-approval gate, not a separate workflow.
  const [editingDraft, setEditingDraft] = useState(null);
  const [savingDraftId, setSavingDraftId] = useState(null);

  const toggleOpp = (id) => {
    setExpandedId((cur) => (cur === id ? null : id));
    setActiveTab("overview");
  };

  const selectTab = (id, tab) => {
    setActiveTab(tab);
    if (tab === "outreach" && drafts[id] === undefined) {
      loadDraftsForCompany(id);
    }
  };

  const opportunities = briefing?.opportunities || [];

  const selectedOpportunities = useMemo(
    () => opportunities.filter((opp) => selectedIds.has(opp.customerCompanyId)),
    [opportunities, selectedIds],
  );

  // Prune selections that disappear after a briefing refresh so the bulk bar
  // and modal never refer to companies that are no longer on the list.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const available = new Set(
        opportunities.map((opp) => opp.customerCompanyId),
      );
      const next = new Set([...prev].filter((id) => available.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [opportunities]);

  const toggleSelected = (customerCompanyId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(customerCompanyId)) next.delete(customerCompanyId);
      else next.add(customerCompanyId);
      return next;
    });
  };

  const clearSelected = () => setSelectedIds(new Set());

  // When a send succeeds the pipeline board has already moved new -> contacted
  // server-side. Mirror that locally so the "Contacted" tag appears without a
  // full briefing reload.
  const markCompaniesContacted = useCallback((customerCompanyIds) => {
    const ids = new Set(
      Array.isArray(customerCompanyIds)
        ? customerCompanyIds
        : [customerCompanyIds],
    );
    if (ids.size === 0) return;
    setBriefing((prev) => {
      if (!prev?.opportunities) return prev;
      const now = new Date().toISOString();
      return {
        ...prev,
        opportunities: prev.opportunities.map((opp) => {
          if (!ids.has(opp.customerCompanyId)) return opp;
          const wasNew = !opp.pipelineStage || opp.pipelineStage === "new";
          return {
            ...opp,
            pipelineStage: wasNew ? "contacted" : opp.pipelineStage,
            pipelineStageChangedAt: wasNew ? now : opp.pipelineStageChangedAt,
            lastContactAt: now,
            daysSinceLastContact: 0,
            touchCount: (opp.touchCount || 0) + 1,
          };
        }),
      };
    });
  }, []);

  const briefingSections = useMemo(
    () => parseBriefingSections(briefing?.summaryText),
    [briefing?.summaryText],
  );

  // Headline numbers for the briefing's KPI strip. Everything derives from
  // the structured briefing payload already loaded - no extra API calls.
  const kpiTiles = useMemo(() => {
    const opps = briefing?.opportunities || [];
    const live = opps.filter(
      (o) =>
        o?.pipelineStage === "qualified" || o?.pipelineStage === "proposal",
    ).length;
    const overdue = opps.filter((o) => o?.accountsOverdue === true).length;
    const tiles = [];
    if (briefing?.estimatedPipelineGbp != null) {
      tiles.push({
        key: "pipeline",
        tone: "accent",
        label: "Est. pipeline",
        value: `£${Math.round(briefing.estimatedPipelineGbp).toLocaleString()}`,
        sub: `across ${opps.length} ${opps.length === 1 ? "priority" : "priorities"}`,
        scope: "Today's priorities · Today",
        hint: "The combined estimated value of today's prioritised opportunities if they all convert.",
      });
    }
    tiles.push({
      key: "priorities",
      tone: "accent",
      label: "Priorities today",
      value: String(opps.length),
      scope: "Today's priorities · Today",
      hint: "How many opportunities AXO has put at the top of today's list for you to work.",
    });
    tiles.push({
      key: "live",
      tone: "green",
      label: "Live deals",
      value: String(live),
      sub: "qualified or proposal",
      scope: "Today's priorities · Today",
      hint: "Opportunities sitting in the qualified or proposal stage, deals that are actively in play.",
    });
    tiles.push({
      key: "overdue",
      tone: "amber",
      label: "Accounts overdue",
      value: String(overdue),
      sub: "Companies House",
      scope: "Today's priorities · Today",
      hint: "Companies whose accounts are overdue at Companies House, often an early sign of financial pressure and a reason to prioritise or approach with care.",
    });
    const aiSpend = briefing?.aiSpendTodayGbp ?? briefing?.aiCostGbp;
    if (aiSpend != null) {
      tiles.push({
        key: "ai-cost",
        tone: "accent",
        label: "Your AI spend today",
        value: `£${Number(aiSpend).toFixed(2)}`,
        scope: "You · Today",
        hint: "What AXO has spent on AI model calls for your account today, shown from your own AI request log.",
      });
    }
    return tiles;
  }, [briefing]);

  // Product filter: tapping a product tile narrows "Where to focus" to it.
  const [productFilter, setProductFilter] = useState(null);
  const [signalFilter, setSignalFilter] = useState("all");
  const [sortBy, setSortBy] = useState("brief");
  const [showAllFocus, setShowAllFocus] = useState(false);
  const FOCUS_PREVIEW_COUNT = 8;

  // One card per opportunity. The AI summary is a separate summary strip;
  // it is never allowed to create "Priority N" / "Notes" placeholder cards or
  // a card whose body lists several companies.
  const focusItems = useMemo(
    () =>
      opportunities.map((opp, i) => {
        const isLive =
          opp?.pipelineStage === "qualified" ||
          opp?.pipelineStage === "proposal";
        const overdue = opp?.accountsOverdue === true;
        const actionText = humanActionLabel(opp?.recommendedAction);
        const body = humaniseSystemValues(
          [
            actionText,
            opp?.recommendedCommunicationMethodLabel,
            opp?.recommendedProductReason,
            ...(Array.isArray(opp?.scoreReasons)
              ? opp.scoreReasons.slice(0, 2)
              : []),
          ]
            .filter(Boolean)
            .join(" · "),
        );
        const title = opp?.companyName || "Unknown company";
        const tone = briefTone(
          [
            title,
            body,
            overdue ? "accounts overdue" : "",
            isLive ? "live deal" : "",
          ]
            .filter(Boolean)
            .join(" "),
        );
        return {
          section: { title, body: body || "No additional notes." },
          opp,
          isLive,
          overdue,
          tone,
          rank: i + 1,
        };
      }),
    [opportunities],
  );

  // KPI cards for the old "briefing notes" block: counts derived from the
  // same structured opportunities, so they stay consistent with the cards.
  const focusKpis = useMemo(() => {
    const now = new Date();
    const end = new Date(now.getTime() + 14 * 86400000);
    const live = opportunities.filter(
      (o) =>
        o?.pipelineStage === "qualified" || o?.pipelineStage === "proposal",
    ).length;
    const overdue = opportunities.filter(
      (o) => o?.accountsOverdue === true,
    ).length;
    const filingsDue = opportunities.filter((o) => {
      if (!o?.nextAccountsDue || o?.accountsOverdue === true) return false;
      const due = new Date(o.nextAccountsDue);
      return !Number.isNaN(due.getTime()) && due >= now && due <= end;
    }).length;
    const siteChanges = opportunities.filter((o) => o?.websiteChange).length;
    const fastWins = opportunities.filter(
      (o) => o?.priorityConfidenceLabel === "high",
    ).length;
    const drafts = opportunities.filter((o) => o?.hasPendingDraft).length;
    return [
      {
        key: "priorities",
        label: "Priorities today",
        value: opportunities.length,
        sub: "ranked companies",
      },
      {
        key: "live",
        label: "Act now",
        value: live,
        sub: "live deals",
      },
      {
        key: "overdue",
        label: "Compliance",
        value: overdue,
        sub: "accounts overdue",
      },
      {
        key: "filings",
        label: "Filings due",
        value: filingsDue,
        sub: "next 14 days",
      },
      {
        key: "website",
        label: "Website changes",
        value: siteChanges,
        sub: "recent signals",
      },
      {
        key: "fast",
        label: drafts ? "Drafts ready" : "Fast wins",
        value: drafts || fastWins,
        sub: drafts ? "waiting for approval" : "high confidence",
      },
    ];
  }, [opportunities]);

  // "By product" = the Phase 5 brief's "18 Wizzyl / 7 AXO Signal / 4 6ELA
  // opportunities": a count of TODAY'S prioritised opportunities per product,
  // grouped by the product AXO recommends for each (recommendedProduct,
  // falling back to the assigned product, matching how the backend groups
  // the pool in gatherOpportunities). Only products that actually have an
  // opportunity today are listed, exactly like the brief's example, so a
  // product never shows a misleading "0". It deliberately does NOT use the
  // raw briefing.byProductCounts numbers, those are the whole addressable
  // prospect pool (tens of thousands), not today's opportunities.
  const serviceNamesByProduct = useMemo(() => {
    const map = {};
    for (const svc of serviceProfiles) {
      if (!svc?.productKey || !svc?.name) continue;
      if (!map[svc.productKey]) map[svc.productKey] = [];
      map[svc.productKey].push(svc.name);
    }
    return map;
  }, [serviceProfiles]);

  const productTiles = useMemo(() => {
    const counts = {};
    opportunities.forEach((o) => {
      // AXO's recommended product only (the scorer's primary), the same
      // classification the main dashboard's Opportunity panel shows. There is
      // deliberately NO fallback to the assigned product: that mislabelled
      // "no clear fit" opportunities as the default product (AXO Signal) and
      // produced a false count.
      const key = o?.recommendedProduct || UNCLASSIFIED_KEY;
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([product, count]) => ({
        key: product,
        label:
          product === UNCLASSIFIED_KEY
            ? "No clear fit"
            : serviceNamesByProduct[product]?.join(" / ") ||
              humanProductLabel(product),
        value: count,
      }))
      .sort((a, b) => {
        if (a.key === UNCLASSIFIED_KEY) return 1;
        if (b.key === UNCLASSIFIED_KEY) return -1;
        return b.value - a.value;
      });
  }, [opportunities, serviceNamesByProduct]);

  // The other number the brief doesn't show but reps ask for: the size of the
  // addressable pool per product (every eligible company, scoped to this
  // user). Surfaced as clearly-labelled secondary text, never as a chip
  // count, so it can't be read as "today's opportunities".
  const poolTiles = useMemo(() => {
    return Object.entries(briefing?.byProductCounts || {})
      .map(([product, count]) => ({
        key: product,
        label:
          product === UNCLASSIFIED_KEY
            ? "No clear fit"
            : serviceNamesByProduct[product]?.join(" / ") ||
              humanProductLabel(product),
        value: Number(count) || 0,
      }))
      .sort((a, b) => {
        if (a.key === UNCLASSIFIED_KEY) return 1;
        if (b.key === UNCLASSIFIED_KEY) return -1;
        return b.value - a.value;
      });
  }, [briefing, serviceNamesByProduct]);

  const byProductItems = useMemo(
    () =>
      productFilter
        ? focusItems.filter(
            (it) =>
              (it.opp?.recommendedProduct || UNCLASSIFIED_KEY) ===
              productFilter,
          )
        : focusItems,
    [focusItems, productFilter],
  );

  const signalCounts = useMemo(
    () => ({
      all: byProductItems.length,
      live: byProductItems.filter((it) => it.isLive).length,
      overdue: byProductItems.filter((it) => it.overdue).length,
      risk: byProductItems.filter((it) => it.tone === "red").length,
    }),
    [byProductItems],
  );

  const visibleItems = useMemo(() => {
    let list = byProductItems;
    if (signalFilter === "live") list = list.filter((it) => it.isLive);
    else if (signalFilter === "overdue") list = list.filter((it) => it.overdue);
    else if (signalFilter === "risk")
      list = list.filter((it) => it.tone === "red");
    if (sortBy === "score")
      list = [...list].sort(
        (x, y) => (y.opp?.priorityScore ?? -1) - (x.opp?.priorityScore ?? -1),
      );
    else if (sortBy === "value")
      list = [...list].sort(
        (x, y) =>
          (y.opp?.estimatedValueGbp ?? -1) - (x.opp?.estimatedValueGbp ?? -1),
      );
    return list;
  }, [byProductItems, signalFilter, sortBy]);

  const previewFocusItems = useMemo(
    () =>
      showAllFocus ? visibleItems : visibleItems.slice(0, FOCUS_PREVIEW_COUNT),
    [showAllFocus, visibleItems],
  );

  // Credit-free best-route suggestions for the briefing cards on screen.
  // Fetches only ids not already loaded.
  useEffect(() => {
    const ids = previewFocusItems
      .map((it) => it.opp?.customerCompanyId)
      .filter((id) => id && !routeSuggestions[id]);
    if (ids.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getRouteSuggestions(ids);
        if (!cancelled && res?.suggestions) {
          setRouteSuggestions((prev) => ({ ...prev, ...res.suggestions }));
        }
      } catch (err) {
        // non-fatal: cards simply show no suggestion
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewFocusItems]);

  // Collapse back to the first page whenever the filter changes.
  useEffect(() => {
    setShowAllFocus(false);
  }, [productFilter, signalFilter, sortBy]);

  const resetFocusFilters = () => {
    setProductFilter(null);
    setSignalFilter("all");
  };

  // Open the full opportunity card from the brief modal: clear search, page to
  // the opportunity and scroll it into view.
  const [jumpId, setJumpId] = useState(null);
  const openOpportunity = (opp) => {
    if (!opp) return;
    setSearchQuery("");
    setJumpId(opp.customerCompanyId);
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 8;

  // Companies House manual "fetch now", per-opportunity refresh, keyed
  // by companyNumber so concurrent refreshes on different cards don't
  // clash. financialsRefreshedAt/Errors are local-only overrides layered
  // on top of the briefing's cached fields until the next full refresh.
  const [refreshingFinancials, setRefreshingFinancials] = useState(null); // companyNumber
  const [financialsOverrides, setFinancialsOverrides] = useState({}); // companyNumber -> { accountsOverdue, companyStatus, nextAccountsDue, lastAccountsMadeUpTo, financialsFetchedAt, latestAccountsFiling, accountingReferenceDate, lastAccountsType, nextAccountsMadeUpTo }
  const [financialsError, setFinancialsError] = useState(null);

  const handleRefreshFinancials = async (companyNumber) => {
    if (!companyNumber || refreshingFinancials) return;
    setRefreshingFinancials(companyNumber);
    setFinancialsError(null);
    try {
      const res = await api.refreshCompanyFinancials(companyNumber);
      // The api client's axios response interceptor already unwraps to
      // response.data, so `res` here IS the controller's JSON body
      // ({ success, data, source }), a single `res?.data`, not
      // `res?.data?.data` (that double-unwrap silently made this always
      // undefined, so the refresh call succeeded server-side, as seen
      // in the fetchNow logs, but the UI never picked up the result
      // until the next full briefing regen).
      const data = res?.data;
      if (data) {
        setFinancialsOverrides((prev) => ({
          ...prev,
          [companyNumber]: {
            accountsOverdue: data.accountsOverdue,
            companyStatus: data.profile?.companyStatus,
            nextAccountsDue: data.profile?.nextAccountsDue,
            lastAccountsMadeUpTo: data.profile?.lastAccountsMadeUpTo,
            latestAccountsFiling: data.latestAccountsFiling,
            accountingReferenceDate: data.profile?.accountingReferenceDate,
            lastAccountsType: data.profile?.lastAccountsType,
            nextAccountsMadeUpTo: data.profile?.nextAccountsMadeUpTo,
            financialsFetchedAt: new Date().toISOString(),
          },
        }));
      }
    } catch (err) {
      setFinancialsError(
        `Failed to refresh Companies House data for ${companyNumber}, ${
          err?.response?.data?.message || err.message
        }`,
      );
    } finally {
      setRefreshingFinancials(null);
    }
  };

  const filteredOpportunities = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return opportunities;
    return opportunities.filter((opp) => {
      const action = humanActionLabel(opp.recommendedAction);
      const product = humanProductLabel(opp.product);
      return (
        opp.companyName?.toLowerCase().includes(q) ||
        product.toLowerCase().includes(q) ||
        action.toLowerCase().includes(q)
      );
    });
  }, [opportunities, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, opportunities]);

  useEffect(() => {
    if (!jumpId || searchQuery) return;
    const idx = opportunities.findIndex((o) => o.customerCompanyId === jumpId);
    if (idx >= 0) setCurrentPage(Math.floor(idx / PAGE_SIZE) + 1);
    setExpandedId(jumpId);
    setJumpId(null);
    setTimeout(() => {
      document.getElementById(`opp-${jumpId}`)?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "start",
      });
    }, 80);
  }, [jumpId, searchQuery, opportunities]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredOpportunities.length / PAGE_SIZE),
  );
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pagedOpportunities = filteredOpportunities.slice(
    pageStart,
    pageStart + PAGE_SIZE,
  );
  const allPageSelected =
    pagedOpportunities.length > 0 &&
    pagedOpportunities.every((opp) => selectedIds.has(opp.customerCompanyId));

  const toggleSelectPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pagedOpportunities.forEach((opp) => next.delete(opp.customerCompanyId));
      } else {
        pagedOpportunities.forEach((opp) => next.add(opp.customerCompanyId));
      }
      return next;
    });
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    const timeOfDay =
      hour < 5
        ? "evening"
        : hour < 12
          ? "morning"
          : hour < 18
            ? "afternoon"
            : "evening";
    const firstName = user?.name?.trim()?.split(/\s+/)[0] || "there";
    return `Good ${timeOfDay}, ${firstName}`;
  };

  // Inline cost preview shared by all three "refresh/generate briefing"
  // entry points (embedded toolbar, header nav, empty state).
  const confirmBriefingCost = briefingEstimate ? (
    <span className="cost-confirm">
      <span className="cost-confirm-amount">
        Est. £{Number(briefingEstimate.estimatedCostGbp || 0).toFixed(2)}
        {briefingEstimate.opportunityCount
          ? ` · ${briefingEstimate.opportunityCount} opportunities`
          : ""}
      </span>
      <button
        className="btn btn-primary btn-sm"
        disabled={generating}
        onClick={handleConfirmBriefingGenerate}
      >
        {generating ? "Generating..." : "Generate"}
      </button>
      <button
        className="btn btn-ghost btn-sm"
        disabled={generating}
        onClick={handleCancelBriefingEstimate}
      >
        Cancel
      </button>
    </span>
  ) : null;

  const subnavSlot = useSubnavSlot();

  const rootInner = (
    <div className={`dash-root${darkMode ? " dark" : ""}`}>
      <style>
        {RESET_CSS}
        {embedded
          ? scopeCss(styles, EMBED_SCOPE) +
            `${EMBED_SCOPE} .dash-root { min-height: 0; background: var(--bg); color: var(--text); }
             ${EMBED_SCOPE} .dash-main { max-width: none; padding: 0; }`
          : styles}
      </style>

      {embedded ? (
        subnavSlot &&
        createPortal(
          confirmBriefingCost || (
            <button
              id="tour-sw-refresh"
              className="btn btn-ghost btn-sm"
              onClick={handleRequestBriefingEstimate}
              disabled={estimatingBriefing || generating}
            >
              {estimatingBriefing
                ? "Estimating..."
                : generating
                  ? "Generating..."
                  : "Refresh briefing"}
            </button>
          ),
          subnavSlot,
        )
      ) : (
        <header className="dash-header">
          <div className="dash-header-inner">
            <div className="dash-brand">
              <Link
                to={backTo}
                className="btn-back"
                title="Back to Dashboard"
                aria-label="Back to Dashboard"
              >
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 12H5M12 5l-7 7 7 7"
                  />
                </svg>
              </Link>
              <div className="dash-brand-logo">
                <img src="/assets/logo.png" alt="AXO" />
              </div>
              <div className="dash-brand-text">
                <div className="dash-brand-name">Sales Workspace</div>
                <div className="dash-brand-sub">AXO Revenue Intelligence</div>
              </div>
            </div>

            <nav className="dash-nav">
              {confirmBriefingCost || (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={handleRequestBriefingEstimate}
                  disabled={estimatingBriefing || generating}
                >
                  {estimatingBriefing
                    ? "Estimating..."
                    : generating
                      ? "Generating..."
                      : "Refresh briefing"}
                </button>
              )}

              <div
                className="settings-wrap"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setSettingsOpen((o) => !o)}
                  aria-haspopup="true"
                  aria-expanded={settingsOpen}
                  onKeyDown={(e) =>
                    e.key === "Escape" && setSettingsOpen(false)
                  }
                >
                  Settings{" "}
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
                <div className={`settings-menu${settingsOpen ? " open" : ""}`}>
                  <div className="settings-row">
                    <span>Dark Mode</span>
                    <ThemeSwitch
                      iconOnly
                      theme={darkMode ? "dark" : "light"}
                      onThemeChange={(next, variant, trigger) =>
                        applyThemeWithTransition(
                          next,
                          trigger,
                          setDarkMode,
                          variant,
                        )
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="user-pill">
                <div className="online-dot" />
                {user?.name}
              </div>
              <button onClick={logout} className="btn btn-ghost btn-sm">
                Logout
              </button>
            </nav>
          </div>
        </header>
      )}

      <div className="dash-main">
        {!embedded && (
          <div className="dashboard-greeting">
            <h1 className="dashboard-greeting-text">{getGreeting()}</h1>
          </div>
        )}

        {loading ? (
          <BriefSkeleton />
        ) : !briefing ? (
          <div className="empty-state">
            <div className="empty-state-label">
              No briefing generated yet for today
            </div>
            {confirmBriefingCost || (
              <button
                className="btn btn-primary btn-md"
                onClick={handleRequestBriefingEstimate}
                disabled={estimatingBriefing || generating}
              >
                {estimatingBriefing
                  ? "Estimating..."
                  : generating
                    ? "Generating..."
                    : "Generate today's briefing"}
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="summary-card">
              <h2 className="summary-title">
                Today's Briefing
                <InfoTooltip
                  label="What is today's briefing?"
                  content="Your daily, prioritised plan: the opportunities most worth working today, why they matter and what AXO suggests you do next."
                />
              </h2>
              <div className="brief-generated">
                Generated{" "}
                {formatDateTime(briefing.generatedAt || briefing.createdAt)}
                {briefing.cacheExpiresAt
                  ? ` · cached until ${formatDateTime(briefing.cacheExpiresAt)}`
                  : ""}
              </div>
              {kpiTiles.length > 0 && (
                <section
                  className="brief-block"
                  id="tour-sw-overview"
                  aria-label="Headline numbers"
                >
                  <h3 className="brief-section-label">
                    Overview
                    <InfoTooltip
                      label="What is the overview?"
                      content="Headline numbers for today's briefing. Each tile explains itself, hover the info icon beside any label."
                    />
                  </h3>
                  <div className="kpi-strip">
                    {kpiTiles.map((t) => (
                      <div className={`kpi-tile ${t.tone}`} key={t.key}>
                        <div className="kpi-tile-head">
                          <span className="kpi-tile-label">
                            {t.label}
                            {t.hint && (
                              <InfoTooltip
                                label={`What is ${t.label}?`}
                                content={t.hint}
                              />
                            )}
                          </span>
                        </div>
                        <div className="kpi-tile-value">{t.value}</div>
                        {t.sub && <div className="kpi-tile-sub">{t.sub}</div>}
                        {t.scope && (
                          <div className="kpi-tile-scope">{t.scope}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}
              {productTiles.length > 0 && (
                <section
                  className="brief-block"
                  id="tour-sw-products"
                  aria-label="Opportunities by product"
                >
                  <div className="brief-section-head">
                    <h3 className="brief-section-label">
                      Opportunities by product
                      <InfoTooltip
                        label="What are opportunities by product?"
                        content="Today's prioritised opportunities grouped by the product AXO recommends for each, using your saved service names where mapped. Select one to narrow the list below."
                      />
                    </h3>
                    {productFilter && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setProductFilter(null)}
                      >
                        Clear filter
                      </button>
                    )}
                  </div>
                  <ChipGroup
                    label="Filter by product"
                    value={productFilter}
                    onChange={setProductFilter}
                    options={productTiles.map((p) => ({
                      value: p.key,
                      label: p.label,
                      count: p.value,
                    }))}
                  />
                  {poolTiles.length > 0 && (
                    <p className="pool-note">
                      Eligible pool · Company book minus exclusions · All time.
                      Not just today&#39;s opportunities:{" "}
                      {poolTiles.map((p, i) => (
                        <span key={p.key}>
                          {i > 0 && " · "}
                          {p.label} <b>{p.value.toLocaleString()}</b>
                        </span>
                      ))}
                    </p>
                  )}
                </section>
              )}
              {opportunities.length > 0 ? (
                <section className="brief-block" aria-label="Where to focus">
                  <div className="brief-section-head">
                    <h3 className="brief-section-label">
                      Where to focus
                      <InfoTooltip
                        label="What is where to focus?"
                        content="Your prioritised opportunities, ranked. The signal filter can narrow to Live deals, Accounts overdue or At risk. At risk means the wording of AXO's research reads as cooling or negative, no response, stalled, churn or going cold, so the deal may need attention."
                      />
                    </h3>
                    <span className="brief-section-count">
                      {visibleItems.length === focusItems.length
                        ? `${focusItems.length} ${focusItems.length === 1 ? "priority" : "priorities"}`
                        : `${visibleItems.length} of ${focusItems.length} priorities`}
                    </span>
                  </div>
                  <div className="brief-summary-strip">
                    <div className="brief-summary-strip-head">
                      Briefing at a glance
                      <span>
                        Generated{" "}
                        {formatDateTime(
                          briefing.generatedAt || briefing.createdAt,
                        )}
                      </span>
                    </div>
                    <div className="brief-summary-kpis">
                      {focusKpis.map((kpi) => (
                        <div className="brief-summary-kpi" key={kpi.key}>
                          <div className="brief-summary-kpi-label">
                            {kpi.label}
                          </div>
                          <div className="brief-summary-kpi-value">
                            {Number(kpi.value).toLocaleString("en-GB")}
                          </div>
                          <div className="brief-summary-kpi-sub">{kpi.sub}</div>
                        </div>
                      ))}
                    </div>
                    {briefing?.summaryText && (
                      <details className="brief-notes-details">
                        <summary>Full briefing notes</summary>
                        <p>{humaniseSystemValues(briefing.summaryText)}</p>
                      </details>
                    )}
                  </div>
                  <div className="brief-toolbar" id="tour-sw-filters">
                    <SegmentedControl
                      label="Filter by signal"
                      value={signalFilter}
                      onChange={setSignalFilter}
                      options={[
                        ["all", "All"],
                        ["live", "Live deals"],
                        ["overdue", "Accounts overdue"],
                        ["risk", "At risk"],
                      ].map(([key, label]) => ({
                        value: key,
                        label,
                        count: signalCounts[key],
                      }))}
                    />
                    <div className="brief-sort">
                      <Select
                        label="Sort by"
                        value={sortBy}
                        onValueChange={setSortBy}
                        options={[
                          { value: "brief", label: "Briefing order" },
                          { value: "score", label: "Priority score" },
                          { value: "value", label: "Est. value" },
                        ]}
                      />
                    </div>
                  </div>
                  {visibleItems.length === 0 ? (
                    <div className="brief-empty">
                      <div>No priorities match these filters.</div>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={resetFocusFilters}
                      >
                        Clear filters
                      </button>
                    </div>
                  ) : (
                    <div className="brief-grid">
                      {previewFocusItems.map((it, idx) => {
                        const { section, opp, isLive, overdue, tone, rank } =
                          it;
                        const productLabel = opp?.product
                          ? humanProductLabel(opp.product)
                          : "General";
                        const value = opp?.estimatedValueGbp;
                        const score = opp?.priorityScore;
                        const title =
                          opp?.companyName ||
                          section.title ||
                          "Unknown company";
                        const briefingRoute = opp?.customerCompanyId
                          ? effectiveRouteSuggestion(
                              routeSuggestions[opp.customerCompanyId],
                              contacts[opp.customerCompanyId],
                            )
                          : null;
                        return (
                          <div
                            className={`brief-card ${tone}`}
                            key={opp?.customerCompanyId || rank}
                            id={idx === 0 ? "tour-sw-card" : undefined}
                          >
                            <div className="brief-card-head">
                              <span className="brief-rank">#{rank}</span>
                              <span className="brief-eyebrow">
                                {productLabel}
                              </span>
                              {score != null && (
                                <>
                                  <span className="brief-score">{score}</span>
                                  <InfoTooltip
                                    label="What is the priority score?"
                                    content={PRIORITY_SCORE_HELP}
                                    className="brief-score-hint"
                                  />
                                </>
                              )}
                            </div>
                            <div className="brief-card-title">{title}</div>
                            {value != null && (
                              <div className="brief-kpi">
                                <span className="brief-kpi-value">
                                  £{Math.round(value).toLocaleString()}
                                </span>
                                <span className="brief-kpi-label">
                                  Est. value
                                  <InfoTooltip
                                    label="What is estimated value?"
                                    content={estimatedValueHelp(opp)}
                                  />
                                </span>
                              </div>
                            )}
                            <div className="brief-turnover">
                              {opp?.turnoverGbp != null ? (
                                <>
                                  Turnover £
                                  {Math.round(opp.turnoverGbp).toLocaleString()}
                                  {opp.turnoverPeriodEnd
                                    ? ` (FY ${String(opp.turnoverPeriodEnd).slice(0, 4)})`
                                    : ""}
                                  {opp.turnoverSource &&
                                  opp.turnoverSource !== "ch_ixbrl" &&
                                  opp.turnoverSource !== "ch_pdf"
                                    ? " · third-party"
                                    : ""}
                                </>
                              ) : (
                                "Turnover not filed"
                              )}
                              {opp?.employees != null &&
                                ` · ${opp.employees} staff`}
                            </div>
                            <p className="brief-card-body" title={section.body}>
                              {section.body}
                            </p>
                            {(overdue ||
                              isLive ||
                              tone === "red" ||
                              (opp &&
                                (opp.lastContactAt || opp.touchCount > 0))) && (
                              <div className="brief-flags">
                                {overdue && (
                                  <span className="flag amber">
                                    Accounts overdue
                                    <InfoTooltip
                                      label="What does accounts overdue mean?"
                                      content="This company's accounts are overdue at Companies House, often an early sign of financial pressure. Approach with care."
                                    />
                                  </span>
                                )}
                                {isLive && (
                                  <span className="flag green">
                                    Live deal
                                    <InfoTooltip
                                      label="What is a live deal?"
                                      content="This opportunity is in the qualified or proposal stage, it's actively in play."
                                    />
                                  </span>
                                )}
                                {opp &&
                                  (opp.lastContactAt || opp.touchCount > 0) && (
                                    <span className="flag blue">
                                      Contacted
                                      <InfoTooltip
                                        label="What does contacted mean?"
                                        content="You have already contacted this company, so the outreach may be a follow-up rather than a first touch."
                                      />
                                    </span>
                                  )}
                                {tone === "red" && (
                                  <span className="flag red">
                                    At risk
                                    <InfoTooltip
                                      label="What does at risk mean?"
                                      content="AXO's research reads as cooling or negative, for example no response, stalled, churn or going cold. The deal may need attention to keep it moving."
                                    />
                                  </span>
                                )}
                              </div>
                            )}
                            {opp && (
                              <div className="brief-footer">
                                <RouteSuggestion
                                  suggestion={
                                    briefingRoute
                                      ? { recommended: briefingRoute }
                                      : null
                                  }
                                />
                                <Button
                                  type="button"
                                  id={idx === 0 ? "tour-sw-brief" : undefined}
                                  variant="secondary"
                                  size="sm"
                                  className="brief-open"
                                  onClick={() => openBriefModal(opp, section)}
                                >
                                  Open brief
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {visibleItems.length > FOCUS_PREVIEW_COUNT && (
                    <div className="brief-show-more">
                      <button
                        type="button"
                        className="btn btn-secondary btn-md"
                        onClick={() => setShowAllFocus((value) => !value)}
                      >
                        {showAllFocus
                          ? "Show less"
                          : `See more (${visibleItems.length - FOCUS_PREVIEW_COUNT} more)`}
                      </button>
                    </div>
                  )}
                </section>
              ) : (
                <div className="summary-text">
                  {humaniseSystemValues(briefing.summaryText)}
                </div>
              )}
            </div>

            {notice && (
              <div
                className={`axo-notice ${notice.tone === "error" ? "error" : ""}`}
                role="status"
              >
                {notice.text}
              </div>
            )}
            {error && (
              <div className="inline-error" role="alert">
                {error}
              </div>
            )}
            {financialsError && (
              <div className="inline-error" role="alert">
                {financialsError}
              </div>
            )}

            {opportunities.length > 0 && (
              <div className="search-row">
                <div className="search-input-wrap">
                  <SearchField
                    label="Search priorities"
                    hideLabel
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                    placeholder="Search by company, product or action"
                  />
                </div>
                <div className="search-count">
                  {filteredOpportunities.length}{" "}
                  {filteredOpportunities.length === 1
                    ? "priority"
                    : "priorities"}
                </div>
              </div>
            )}

            {opportunities.length > 0 && (
              <div className="bulk-bar">
                {selectedIds.size === 0 ? (
                  <>
                    <span className="bulk-bar-hint">
                      Select companies below to prepare and send their outreach
                      emails together.
                    </span>
                    <div className="bulk-bar-actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={toggleSelectPage}
                      >
                        Select all on this page
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="bulk-bar-count">
                      {selectedIds.size} selected
                    </span>
                    <span className="bulk-bar-hint">
                      Prepare, edit and send email drafts in one batch.
                    </span>
                    <div className="bulk-bar-actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={toggleSelectPage}
                      >
                        {allPageSelected
                          ? "Deselect page"
                          : "Select all on this page"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={clearSelected}
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        id="tour-sw-bulk"
                        onClick={openBulkOutreach}
                      >
                        Bulk outreach
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            <div
              className={
                opportunities.length === 0 || filteredOpportunities.length === 0
                  ? "table-wrap"
                  : "opp-list"
              }
            >
              {opportunities.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-title">No priorities today</div>
                  <div className="empty-sub">
                    Once AXO generates a briefing, your priorities will appear
                    here.
                  </div>
                </div>
              ) : filteredOpportunities.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-title">
                    No priorities match "{searchQuery}"
                  </div>
                  <div className="empty-sub">
                    Try a different search term, or clear the search to see all
                    priorities.
                  </div>
                </div>
              ) : (
                pagedOpportunities.map((opp) => {
                  const ccDrafts = drafts[opp.customerCompanyId] || [];
                  const routeSuggestion = effectiveRouteSuggestion(
                    routeSuggestions[opp.customerCompanyId],
                    contacts[opp.customerCompanyId],
                  );
                  const financialsOverride =
                    (opp.companyNumber &&
                      financialsOverrides[opp.companyNumber]) ||
                    null;
                  const accountsOverdue =
                    financialsOverride?.accountsOverdue ?? opp.accountsOverdue;
                  const companyStatus =
                    financialsOverride?.companyStatus ?? opp.companyStatus;
                  const nextAccountsDue =
                    financialsOverride?.nextAccountsDue ?? opp.nextAccountsDue;
                  const lastAccountsMadeUpTo =
                    financialsOverride?.lastAccountsMadeUpTo ??
                    opp.lastAccountsMadeUpTo;
                  const financialsFetchedAt =
                    financialsOverride?.financialsFetchedAt ??
                    opp.financialsFetchedAt;
                  const latestAccountsFiling =
                    financialsOverride?.latestAccountsFiling ??
                    opp.latestAccountsFiling;
                  const accountingReferenceDate =
                    financialsOverride?.accountingReferenceDate ??
                    opp.accountingReferenceDate;
                  const nextAccountsMadeUpTo =
                    financialsOverride?.nextAccountsMadeUpTo ??
                    opp.nextAccountsMadeUpTo;
                  const hasFilingData =
                    companyStatus != null ||
                    accountsOverdue != null ||
                    nextAccountsDue != null ||
                    lastAccountsMadeUpTo != null ||
                    latestAccountsFiling != null;
                  const isRefreshingThis =
                    refreshingFinancials === opp.companyNumber;
                  const isOpen = expandedId === opp.customerCompanyId;
                  const hasContactHistory = Boolean(
                    opp.lastContactAt || opp.touchCount > 0,
                  );
                  const productLabel = humanProductLabel(opp.product);
                  const actionLabel = humanActionLabel(opp.recommendedAction);
                  const metaParts = [
                    actionLabel,
                    opp.recommendedCommunicationMethodLabel
                      ? `${opp.recommendedCommunicationMethodLabel} recommended`
                      : null,
                    productLabel,
                  ].filter(Boolean);
                  const liveElsewhere = opp.companyFootprint?.liveDealElsewhere;
                  const sharedCount = opp.companyFootprint?.otherPartnerCount;
                  const tabs = [
                    ["overview", "Overview"],
                    ["outreach", "Outreach", ccDrafts.length || null],
                    [
                      "replies",
                      "Replies",
                      opp.hasReplied ? opp.replyCount : null,
                    ],
                    ["research", "Research"],
                    ["channels", "Channels"],
                  ];
                  const facts = [
                    ["Product", productLabel || "-"],
                    [
                      "Next step",
                      actionLabel
                        ? `${actionLabel}${
                            opp.recommendedCommunicationMethodLabel
                              ? ` · ${opp.recommendedCommunicationMethodLabel}`
                              : ""
                          }`
                        : "-",
                    ],
                    [
                      "Last contact",
                      opp.daysSinceLastContact != null
                        ? `${opp.daysSinceLastContact} days ago`
                        : "-",
                    ],
                    [
                      "Stage",
                      STAGE_LABELS[opp.pipelineStage] ||
                        opp.pipelineStage ||
                        "New",
                    ],
                  ];
                  const chFacts = [
                    companyStatus && [
                      "Status",
                      companyStatus,
                      companyStatus === "active" ? "" : "warn",
                    ],
                    nextAccountsDue && [
                      "Accounts due",
                      formatFilingDate(nextAccountsDue),
                    ],
                    lastAccountsMadeUpTo && [
                      "Last filed",
                      `${formatFilingDate(lastAccountsMadeUpTo)}${
                        formatFilingTypeLabel(latestAccountsFiling)
                          ? ` (${formatFilingTypeLabel(latestAccountsFiling)})`
                          : ""
                      }`,
                    ],
                    nextAccountsMadeUpTo && [
                      "Next period ends",
                      formatFilingDate(nextAccountsMadeUpTo),
                    ],
                    formatAccountingReferenceDate(accountingReferenceDate) && [
                      "Year-end",
                      formatAccountingReferenceDate(accountingReferenceDate),
                    ],
                    accountsOverdue === true && ["Accounts", "Overdue", "warn"],
                    financialsFetchedAt && [
                      "Checked",
                      formatFetchedAt(financialsFetchedAt),
                    ],
                  ].filter(Boolean);

                  return (
                    <div
                      id={`opp-${opp.customerCompanyId}`}
                      className={`opp-card${isOpen ? " open" : ""}`}
                      key={opp.customerCompanyId}
                    >
                      <div className="opp-head-row">
                        <span className="opp-select">
                          <Checkbox
                            aria-label={`Select ${opp.companyName} for bulk outreach`}
                            checked={selectedIds.has(opp.customerCompanyId)}
                            onCheckedChange={() =>
                              toggleSelected(opp.customerCompanyId)
                            }
                          />
                        </span>
                        <button
                          type="button"
                          className="opp-head"
                          aria-expanded={isOpen}
                          onClick={() => toggleOpp(opp.customerCompanyId)}
                        >
                          <span
                            className="opp-avatar"
                            style={{
                              background: `${avatarTint(opp.companyName)}33`,
                            }}
                            aria-hidden="true"
                          >
                            {companyInitials(opp.companyName)}
                          </span>
                          <span className="opp-head-main">
                            <span className="opp-title">{opp.companyName}</span>
                            <span className="opp-meta">
                              {metaParts.join(" · ")}
                            </span>
                          </span>
                          <span className="opp-head-side">
                            {liveElsewhere && (
                              <span
                                className="flag red"
                                title="A live deal for this company is already open in another partner's book."
                              >
                                Live deal elsewhere
                              </span>
                            )}
                            {accountsOverdue === true && (
                              <span
                                className="flag amber"
                                title="Accounts overdue at Companies House."
                              >
                                Accounts overdue
                              </span>
                            )}
                            {hasContactHistory &&
                              opp.pipelineStage !== "contacted" && (
                                <span
                                  className="flag blue"
                                  title={
                                    opp.daysSinceLastContact != null
                                      ? `You have contacted this company before (last contact ${opp.daysSinceLastContact} day${
                                          opp.daysSinceLastContact === 1
                                            ? ""
                                            : "s"
                                        } ago).`
                                      : "You have contacted this company before."
                                  }
                                >
                                  Contacted
                                </span>
                              )}
                            {opp.meetingBookedAt && (
                              <span
                                className="flag blue"
                                title={`Meeting booked ${formatDateTime(opp.meetingBookedAt)}`}
                              >
                                Meeting booked
                              </span>
                            )}
                            {opp.pipelineStage &&
                              opp.pipelineStage !== "new" && (
                                <span
                                  className="flag green"
                                  title="Pipeline stage"
                                >
                                  {STAGE_LABELS[opp.pipelineStage] ||
                                    opp.pipelineStage}
                                </span>
                              )}
                            {opp.lastReplyClassification && (
                              <span
                                className={`reply-badge ${opp.lastReplyClassification}`}
                                title="Classification of the most recent reply"
                              >
                                {REPLY_LABELS[opp.lastReplyClassification] ||
                                  opp.lastReplyClassification}
                              </span>
                            )}
                            <span
                              className="opp-score"
                              title={PRIORITY_SCORE_HELP}
                            >
                              {opp.priorityScore}
                            </span>
                            <svg
                              className="opp-chevron"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M6 9l6 6 6-6" />
                            </svg>
                          </span>
                        </button>
                      </div>

                      {isOpen && (
                        <div className="opp-body">
                          <Tabs
                            value={activeTab}
                            onValueChange={(id) =>
                              selectTab(opp.customerCompanyId, id)
                            }
                          >
                            <div className="opp-tabs-wrap">
                              <TabsList aria-label="Opportunity sections">
                                {tabs.map(([id, label, count]) => (
                                  <TabsTrigger key={id} value={id}>
                                    {label}
                                    {count != null && (
                                      <span
                                        style={{
                                          marginLeft: 6,
                                          color: "var(--text-faint)",
                                          fontVariantNumeric: "tabular-nums",
                                        }}
                                      >
                                        {count}
                                      </span>
                                    )}
                                  </TabsTrigger>
                                ))}
                              </TabsList>
                            </div>

                            <TabsContent value="overview" className="opp-pane">
                              {(liveElsewhere ||
                                (!liveElsewhere && sharedCount > 0) ||
                                (opp.productMismatch &&
                                  opp.recommendedProductLabel)) && (
                                <div className="opp-notes">
                                  {liveElsewhere && (
                                    <div className="opp-note red">
                                      A live deal for this company is already
                                      open in another partner's book.
                                    </div>
                                  )}
                                  {!liveElsewhere && sharedCount > 0 && (
                                    <div className="opp-note amber">
                                      Also in {sharedCount} other partner
                                      {sharedCount === 1 ? "'s" : "s'"} book
                                      {sharedCount === 1 ? "" : "s"}, expect
                                      possible duplicate outreach.
                                    </div>
                                  )}
                                  {opp.productMismatch &&
                                    opp.recommendedProductLabel && (
                                      <div
                                        className="opp-note amber"
                                        title={
                                          opp.recommendedProductReason ||
                                          undefined
                                        }
                                      >
                                        <span>
                                          Scoring suggests{" "}
                                          <strong>
                                            {opp.recommendedProductLabel}
                                          </strong>{" "}
                                          may fit better than {productLabel}.
                                          {opp.recommendedProductReason
                                            ? ` ${[
                                                ...new Set(
                                                  opp.recommendedProductReason
                                                    .split(/;\s*/)
                                                    .filter(Boolean),
                                                ),
                                              ].join("; ")}`
                                            : ""}
                                        </span>
                                      </div>
                                    )}
                                </div>
                              )}

                              <dl className="facts">
                                {facts.map(([k, v]) => (
                                  <div className="fact" key={k}>
                                    <dt>{k}</dt>
                                    <dd>{v}</dd>
                                  </div>
                                ))}
                                {opp.priorityConfidenceLabel && (
                                  <div className="fact">
                                    <dt>Score confidence</dt>
                                    <dd
                                      className={`conf-${opp.priorityConfidenceLabel}`}
                                      title={`${opp.priorityConfidenceScore}/100, how many of the relevance/intent/site-health/contact-history signals are available for this company.`}
                                    >
                                      {opp.priorityConfidenceLabel}
                                    </dd>
                                  </div>
                                )}
                                {opp.meetingBookedAt && (
                                  <div className="fact">
                                    <dt>Meeting</dt>
                                    <dd>
                                      {formatDateTime(opp.meetingBookedAt)}
                                    </dd>
                                  </div>
                                )}
                              </dl>

                              {opp.companyNumber && (
                                <div>
                                  <div className="pane-head">
                                    <span className="pane-label">
                                      Companies House
                                    </span>
                                    <button
                                      type="button"
                                      className="btn btn-ghost btn-sm"
                                      onClick={() =>
                                        handleRefreshFinancials(
                                          opp.companyNumber,
                                        )
                                      }
                                      disabled={isRefreshingThis}
                                      title="Fetch the latest filing data now"
                                    >
                                      {isRefreshingThis
                                        ? "Checking…"
                                        : "Refresh"}
                                    </button>
                                  </div>
                                  {hasFilingData && chFacts.length > 0 ? (
                                    <dl className="facts">
                                      {chFacts.map(([k, v, tone]) => (
                                        <div className="fact" key={k}>
                                          <dt>{k}</dt>
                                          <dd className={tone || undefined}>
                                            {v}
                                          </dd>
                                        </div>
                                      ))}
                                    </dl>
                                  ) : (
                                    <div className="facts-empty">
                                      No filing data cached yet.
                                    </div>
                                  )}
                                </div>
                              )}

                              <div className="opp-footer">
                                {outcomeConfirmedFor ===
                                opp.customerCompanyId ? (
                                  <span className="outcome-confirmed">
                                    Outcome logged
                                  </span>
                                ) : outcomeFormFor === opp.customerCompanyId ? (
                                  <span className="outcome-form">
                                    <button
                                      className="btn btn-ghost btn-md"
                                      disabled={recordingOutcome}
                                      onClick={() =>
                                        handleRecordOutcome(
                                          opp.customerCompanyId,
                                          "meeting_booked",
                                        )
                                      }
                                    >
                                      Meeting booked
                                    </button>
                                    <Input
                                      label="Deal value"
                                      hideLabel
                                      type="number"
                                      className="outcome-value-input"
                                      placeholder="Deal value £"
                                      min="0"
                                      step="0.01"
                                      value={outcomeDealValue}
                                      onChange={(e) =>
                                        setOutcomeDealValue(e.target.value)
                                      }
                                    />
                                    <button
                                      className="btn btn-primary btn-md"
                                      disabled={
                                        recordingOutcome || !outcomeDealValue
                                      }
                                      onClick={() =>
                                        handleRecordOutcome(
                                          opp.customerCompanyId,
                                          "deal_won",
                                        )
                                      }
                                    >
                                      {recordingOutcome
                                        ? "Logging..."
                                        : "Deal won"}
                                    </button>
                                    <button
                                      className="btn btn-ghost btn-md"
                                      onClick={() => {
                                        setOutcomeFormFor(null);
                                        setOutcomeDealValue("");
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </span>
                                ) : (
                                  <button
                                    className="btn btn-ghost btn-md"
                                    onClick={() =>
                                      setOutcomeFormFor(opp.customerCompanyId)
                                    }
                                  >
                                    Log outcome
                                  </button>
                                )}
                              </div>
                            </TabsContent>

                            <TabsContent value="outreach" className="opp-pane">
                              <div>
                                <div className="pane-head">
                                  <span className="pane-label">New draft</span>
                                </div>
                                <div className="draft-setup">
                                  {(() => {
                                    const contact =
                                      contacts[opp.customerCompanyId];
                                    if (!contact) return null;
                                    const emailOk =
                                      contact.hasValidEmail !== undefined
                                        ? contact.hasValidEmail
                                        : contact.hasEmail;
                                    const missing = [
                                      !emailOk && "email",
                                      !contact.hasLinkedin && "LinkedIn page",
                                      !contact.hasPhone && "phone number",
                                    ].filter(Boolean);
                                    if (missing.length === 0) return null;
                                    const searching =
                                      findingEmailFor === opp.customerCompanyId;
                                    return (
                                      <div className="contact-gap">
                                        <span>
                                          Missing: {missing.join(", ")}.
                                          {!emailOk &&
                                            (contact.hasEmail
                                              ? ` Stored email is not verified: ${contact.emailValidation?.reason || "validation failed"}.`
                                              : " Email and follow-up drafts are unavailable until an email is found.")}
                                          {!contact.hasLinkedin &&
                                            " Without a LinkedIn page, “Copy & open LinkedIn” opens a company search instead."}
                                        </span>
                                        <button
                                          className="btn btn-primary btn-sm"
                                          disabled={searching}
                                          onClick={() =>
                                            handleFindEmail(
                                              opp.customerCompanyId,
                                            )
                                          }
                                        >
                                          {searching
                                            ? "Searching…"
                                            : "Find contact details"}
                                        </button>
                                      </div>
                                    );
                                  })()}
                                  {routeSuggestion && (
                                    <div className="route-banner" role="note">
                                      <span className="route-banner-label">
                                        Best route: {routeSuggestion.label}
                                      </span>
                                      <span className="route-banner-reason">
                                        {routeSuggestion.reason}
                                      </span>
                                    </div>
                                  )}
                                  <div className="draft-picker">
                                    {Object.keys(DRAFT_TYPE_LABELS).map(
                                      (draftType) => {
                                        const key = `${opp.customerCompanyId}:${draftType}`;
                                        if (pendingEstimate?.key === key) {
                                          return (
                                            <span
                                              className="cost-confirm"
                                              key={draftType}
                                            >
                                              <span className="cost-confirm-amount">
                                                {DRAFT_TYPE_LABELS[draftType]} ·
                                                Est. £
                                                {pendingEstimate.estimatedCostGbp.toFixed(
                                                  2,
                                                )}
                                              </span>
                                              <button
                                                className="btn btn-primary btn-sm"
                                                disabled={preparingKey === key}
                                                onClick={handleConfirmGenerate}
                                              >
                                                {preparingKey === key
                                                  ? "Generating..."
                                                  : "Generate"}
                                              </button>
                                              <button
                                                className="btn btn-ghost btn-sm"
                                                disabled={preparingKey === key}
                                                onClick={handleCancelEstimate}
                                              >
                                                Cancel
                                              </button>
                                            </span>
                                          );
                                        }
                                        return (
                                          <Button
                                            key={draftType}
                                            variant={
                                              routeSuggestion?.draftType ===
                                              draftType
                                                ? "primary"
                                                : "secondary"
                                            }
                                            size="md"
                                            type="button"
                                            disabled={
                                              Boolean(preparingKey) ||
                                              estimatingKey === key ||
                                              Boolean(
                                                contactMissing(
                                                  contacts[
                                                    opp.customerCompanyId
                                                  ],
                                                  draftType,
                                                ),
                                              )
                                            }
                                            title={
                                              contactMissing(
                                                contacts[opp.customerCompanyId],
                                                draftType,
                                              )
                                                ? `No ${contactMissing(contacts[opp.customerCompanyId], draftType)} on file`
                                                : undefined
                                            }
                                            onClick={() =>
                                              handleRequestEstimate(
                                                opp.customerCompanyId,
                                                draftType,
                                              )
                                            }
                                          >
                                            {estimatingKey === key
                                              ? "Checking cost…"
                                              : DRAFT_TYPE_LABELS[draftType]}
                                          </Button>
                                        );
                                      },
                                    )}
                                  </div>
                                </div>
                              </div>

                              {preparingKey &&
                                preparingKey.startsWith(
                                  `${opp.customerCompanyId}:`,
                                ) && (
                                  <div
                                    className="draft-generating"
                                    role="status"
                                    aria-live="polite"
                                  >
                                    <span className="spin-dot" />
                                    <div>
                                      <div>
                                        {GENERATING_LABELS[
                                          preparingKey.split(":")[1]
                                        ] || "Creating your draft"}
                                        …
                                      </div>
                                      <div className="draft-generating-sub">
                                        This usually takes a few seconds. It
                                        will appear here when ready.
                                      </div>
                                    </div>
                                  </div>
                                )}
                              {ccDrafts.length === 0 ? (
                                !preparingKey && (
                                  <div className="pane-empty">
                                    No drafts yet.
                                  </div>
                                )
                              ) : (
                                <div className="draft-list">
                                  {ccDrafts.map((draft) => {
                                    const isEditingThis =
                                      editingDraft?.id === draft.id;
                                    return (
                                      <div className="draft-box" key={draft.id}>
                                        {isEditingThis ? (
                                          <div className="draft-edit">
                                            {[
                                              "outreach_email",
                                              "outreach_follow_up",
                                            ].includes(draft.draftType) && (
                                              <div>
                                                <label
                                                  className="draft-edit-label"
                                                  htmlFor={`draft-subject-${draft.id}`}
                                                >
                                                  Subject
                                                </label>
                                                <Input
                                                  id={`draft-subject-${draft.id}`}
                                                  label="Subject"
                                                  hideLabel
                                                  value={editingDraft.subject}
                                                  onChange={(e) =>
                                                    setEditingDraft((prev) => ({
                                                      ...prev,
                                                      subject: e.target.value,
                                                    }))
                                                  }
                                                />
                                              </div>
                                            )}
                                            <div>
                                              <label
                                                className="draft-edit-label"
                                                htmlFor={`draft-body-${draft.id}`}
                                              >
                                                {DRAFT_TYPE_LABELS[
                                                  draft.draftType
                                                ] || "Draft"}{" "}
                                                body
                                              </label>
                                              <Textarea
                                                id={`draft-body-${draft.id}`}
                                                label="Email body"
                                                hideLabel
                                                rows={8}
                                                value={editingDraft.content}
                                                onChange={(e) =>
                                                  setEditingDraft((prev) => ({
                                                    ...prev,
                                                    content: e.target.value,
                                                  }))
                                                }
                                              />
                                            </div>
                                            <div className="draft-edit-actions">
                                              <button
                                                className="btn btn-primary btn-md"
                                                disabled={
                                                  savingDraftId === draft.id
                                                }
                                                onClick={() =>
                                                  handleSaveDraft(
                                                    opp.customerCompanyId,
                                                  )
                                                }
                                              >
                                                {savingDraftId === draft.id
                                                  ? "Saving…"
                                                  : "Save draft"}
                                              </button>
                                              <button
                                                className="btn btn-ghost btn-md"
                                                disabled={
                                                  savingDraftId === draft.id
                                                }
                                                onClick={handleCancelEditDraft}
                                              >
                                                Cancel
                                              </button>
                                              <span className="draft-edit-note">
                                                Changes apply to this draft
                                                only. Nothing is sent until you
                                                approve it.
                                              </span>
                                            </div>
                                          </div>
                                        ) : (
                                          <>
                                            {draft.subject && (
                                              <div className="draft-subject">
                                                {draft.subject}
                                              </div>
                                            )}
                                            <div className="draft-content">
                                              {draft.content}
                                            </div>
                                            <span
                                              className={`draft-status ${draft.status}`}
                                            >
                                              {
                                                DRAFT_TYPE_LABELS[
                                                  draft.draftType
                                                ]
                                              }{" "}
                                              · {draft.status}
                                            </span>
                                            {draft.status === "draft" && (
                                              <div className="draft-actions">
                                                {[
                                                  "outreach_email",
                                                  "outreach_follow_up",
                                                ].includes(draft.draftType) && (
                                                  <button
                                                    className="btn btn-primary btn-md"
                                                    disabled={
                                                      sendingId === draft.id ||
                                                      Boolean(
                                                        draftSendIssue(
                                                          draft,
                                                          contacts[
                                                            opp
                                                              .customerCompanyId
                                                          ],
                                                        ),
                                                      )
                                                    }
                                                    title={
                                                      draftSendIssue(
                                                        draft,
                                                        contacts[
                                                          opp.customerCompanyId
                                                        ],
                                                      ) || undefined
                                                    }
                                                    onClick={() =>
                                                      handleApproveAndSend(
                                                        opp.customerCompanyId,
                                                        draft.id,
                                                      )
                                                    }
                                                  >
                                                    {sendingId === draft.id
                                                      ? "Sending…"
                                                      : "Approve & send"}
                                                  </button>
                                                )}
                                                {[
                                                  "outreach_email",
                                                  "outreach_follow_up",
                                                ].includes(draft.draftType) &&
                                                  contacts[
                                                    opp.customerCompanyId
                                                  ]?.email && (
                                                    <span className="draft-recipient">
                                                      To{" "}
                                                      {draft.recipientEmail ||
                                                        contacts[
                                                          opp.customerCompanyId
                                                        ].email}
                                                    </span>
                                                  )}
                                                {draftSendIssue(
                                                  draft,
                                                  contacts[
                                                    opp.customerCompanyId
                                                  ],
                                                ) && (
                                                  <span
                                                    className="draft-recipient"
                                                    style={{
                                                      color: "var(--red)",
                                                    }}
                                                  >
                                                    {draftSendIssue(
                                                      draft,
                                                      contacts[
                                                        opp.customerCompanyId
                                                      ],
                                                    )}
                                                  </span>
                                                )}
                                                {draft.draftType ===
                                                  "outreach_linkedin" && (
                                                  <button
                                                    className="btn btn-ghost btn-md"
                                                    onClick={() =>
                                                      handleCopyAndOpenLinkedIn(
                                                        draft,
                                                        contacts[
                                                          opp.customerCompanyId
                                                        ],
                                                        opp.companyName,
                                                      )
                                                    }
                                                  >
                                                    Copy & open LinkedIn
                                                  </button>
                                                )}
                                                {[
                                                  "outreach_linkedin",
                                                  "outreach_cold_call_intro",
                                                ].includes(draft.draftType) && (
                                                  <button
                                                    className="btn btn-primary btn-md"
                                                    disabled={
                                                      sendingId === draft.id
                                                    }
                                                    onClick={() =>
                                                      handleApproveAndSend(
                                                        opp.customerCompanyId,
                                                        draft.id,
                                                      )
                                                    }
                                                  >
                                                    {sendingId === draft.id
                                                      ? "Saving..."
                                                      : draft.draftType ===
                                                          "outreach_linkedin"
                                                        ? "Mark LinkedIn sent"
                                                        : "Mark call done"}
                                                  </button>
                                                )}
                                                <button
                                                  className="btn btn-ghost btn-md"
                                                  onClick={() =>
                                                    handleStartEditDraft(draft)
                                                  }
                                                >
                                                  Edit
                                                </button>
                                                <button
                                                  className="btn btn-ghost btn-md"
                                                  onClick={() =>
                                                    handleReject(
                                                      opp.customerCompanyId,
                                                      draft.id,
                                                    )
                                                  }
                                                >
                                                  Reject
                                                </button>
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </TabsContent>

                            <OpportunityExtras
                              opp={opp}
                              activeTab={activeTab}
                              channelRefreshSignal={channelRefresh}
                            />
                          </Tabs>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {filteredOpportunities.length > PAGE_SIZE && (
              <div className="pagination-row">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="pagination-status">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <BulkOutreachModal
        open={bulkOpen}
        opportunities={bulkOpportunities}
        onClose={closeBulkOutreach}
        onDraftsChanged={(customerCompanyIds) => {
          customerCompanyIds.forEach((id) => loadDraftsForCompany(id));
        }}
        onSent={handleBulkSent}
      />

      {briefModal && (
        <OpenBriefModal
          opportunity={briefModal.opp}
          productLabel={humanProductLabel(briefModal.opp.product)}
          stageLabel={
            STAGE_LABELS[briefModal.opp.pipelineStage] ||
            briefModal.opp.pipelineStage
          }
          nextStep={[
            humanActionLabel(briefModal.opp.recommendedAction),
            briefModal.opp.recommendedCommunicationMethodLabel,
          ]
            .filter(Boolean)
            .join(" · ")}
          briefText={briefModal.section?.body}
          signals={
            Array.isArray(briefModal.opp.scoreReasons)
              ? briefModal.opp.scoreReasons
              : []
          }
          contact={contacts[briefModal.opp.customerCompanyId]}
          draft={(drafts[briefModal.opp.customerCompanyId] || [])[0]}
          loading={briefModalLoading}
          onClose={() => {
            setBriefModal(null);
            setBriefModalLoading(false);
          }}
          onOpenFull={(opp) => openOpportunity(opp)}
        />
      )}
    </div>
  );

  return embedded ? (
    <div className="ri-embed-sales">{rootInner}</div>
  ) : (
    rootInner
  );
}
